/**
 * Provides a framework for building JavaScript UIs.  Typically these UIs feature a toolstrip above a working area,
 * optionally containing panels on the sides and bottom surrounding documents in the center.
 * Features of the UIContainer include:
 *
 * <ul>
 *     <li>Layout management, including ability of the user to customize layout</li>
 *     <li>Context management whereby UI elements appear and disappear in response to context changes (typically document selection)
 *     These elements include toolstrip tabs, quick access toolbar buttons and side and bottom panels.</li>
 *     <li>Focus management</li>
 *     <li>Docking and undocking</li>
 *     <li>Layout persistence</li>
 * </ul>
 *
 * The UIContainer's behavior is governed by a number of client settable properties.  Some of these properties are set
 * on the container itself, while others are set on children added to the container.  The container-level properties
 * are documented below.  The properties that are set on children are documented in the ChildProperties and DocumentTypeProperties
 * classes.
 *
 * Container level properties may be fixed, meaning that they should be set at construction time and not changed or
 * dynamic meaning that they can be changed at any time.  Since the UIContainer mixes in "Stateful", get, set and
 * watch methods can be used to manage and track dynamic properties.
 *
 * @module MW/uiframework/UIContainer
 * @copyright Copyright 2015-2020 The MathWorks, Inc.
 */
define([
    "dojo/_base/declare",
    "dojo/_base/lang",
    "dojo/dom",
    "dojo/dom-class",
    "dojo/dom-construct",
    "dojo/dom-style",
    "dojo/Evented",
    "dojo/on",
    "dojo/Stateful",
    "dojo/when",
    "dijit/layout/ContentPane",
    "dijit/popup",
    "dijit/registry",
    "mw-actiondataservice/ActionDataService",
    "mw-keybindings/KeyBindings",
    "mw-keybindings/KeyBindingsFactory",
    "mw-log/Log",
    "mw-messageservice/MessageService",
    "mw-form/PushButton",
    "MW/layout/BorderBar",
    "MW/layout/BorderContainer",
    "MW/layout/LayoutContainerEvent",
    "MW/layout/DocumentBar",
    "MW/layout/DocumentContainer",
    "MW/layout/DocumentContainerActions",
    "MW/layout/DocumentGroupProperties",
    "MW/layout/DocumentGroupRegistry",
    "MW/layout/LayoutUtils",
    "MW/layout/LayoutChildProperty",
    "MW/layout/ArrangeableBorderContainer",
    "MW/layout/SelectionManager",
    "MW/layout/StatefulExtension",
    "MW/statusbar/widgets/StatusBar",
    "MW/toolstrip/constants/DisplayStateConstants",
    "MW/toolstrip/constants/TypeConstants",
    "MW/uiframework/CssLoader",
    "MW/uiframework/FocusManager",
    "MW/uiframework/UIBuilderFactory",
    "MW/uiframework/contextmanager/ContextManagerFactory",
    "MW/uiframework/MnemonicsManager",
    "MW/uiframework/MnemonicsStates",
    "MW/uiframework/uicontainer/ChildProperties",
    "MW/uiframework/uicontainer/ChildWindowManager",
    "MW/uiframework/uicontainer/CleanupManager",
    "MW/uiframework/uicontainer/ContextDriver",
    "MW/uiframework/uicontainer/DockingManager",
    "MW/uiframework/uicontainer/DocumentTypeProperties",
    "MW/uiframework/uicontainer/LocalSessionDataService",
    "MW/uiframework/uicontainer/WindowUtils",
    "MW/uiframework/uicontainer/peer/UIContainerPeer",
    "MW/uiframework/utils",
    "mw-overlay-utils/BusyOverlay",
    "mw-utils/allApprove",
    "mw-ddux/UsageLogger",
    "mw-ddux/UsageOverlay",
    "dojo/i18n!l10n/nls/layout",
    "exports"
], function (declare, lang, dom, domClass, domConstruct, domStyle, Evented, on, Stateful, when, ContentPane, popup, registry, ActionDataService,
             KeyBindings, KeyBindingsFactory, Log, MessageService, PushButton, BorderBar, BorderContainer, LayoutContainerEvent,
             DocumentBar, DocumentContainer, DocumentContainerActions, DocumentGroupProperties, DocumentGroupRegistry, LayoutUtils,
             LayoutChildProperty, ArrangeableBorderContainer, SelectionManager, StatefulExtension, StatusBar, DisplayStateConstants, TypeConstants,
             CssLoader, FocusManager, UIBuilderFactory, ContextManagerFactory, MnemonicsManager, MnemonicsStates, ChildProperties,
             ChildWindowManager, CleanupManager, ContextDriver, DockingManager, DocumentTypeProperties, LocalSessionDataService,
             WindowUtils, UIContainerPeer, utils, BusyOverlay, allApprove, UsageLogger, UsageOverlay, layoutL10n, exports) {

    var DOCUMENT_LAYOUT = "documentLayout";
    var FOOTER = "footer";
    var IS_BUSY = "isBusy";
    var IS_TOOLSTRIP_COLLAPSED = "isToolstripCollapsed";
    var IS_VISIBLE = "isVisible";
    var LAST_SELECTED_DOCUMENT = "lastSelectedDocument";
    var LAYOUT = "layout";
    var MAJOR_VERSION = 2;
    var PANEL_LAYOUT = "panelLayout";
    var SELECTED_TOOLSTRIP_TAB = "selectedToolstripTab";
    var TITLE = "title";
    var WINDOW_BOUNDS = "windowBounds";

    var STARTING = "starting";
    var STARTED = "started";
    var STOPPING = "stopping";
    var STOPPED = "stopped";

    // TODO: Remove the next two lines when the focus issues are resolved and select the ROOT_SELECTOR using the getRootNode function
    var ROOT_CLASS = "keyboardNavigation";
    var ROOT_SELECTOR = "." + ROOT_CLASS; // CSS selector for the key bindings API used for keyboard accessibility

    var TOOLSTRIP_ID = "toolstripWrapper";

    /**
     * @class MW/uiframework/UIContainer
     * @classdesc Defines a container for hosting the contents of a UI
     */
    var UIContainer = declare([Evented, Stateful, StatefulExtension], {

        // Fixed Properties
        /**
         * @property {ActionDataService} actionService     service to which custom actions, such as those backing toolstrip controls can be added.
         *                                                 The UIContainer creates an actionService by default but one may be provided via the
         *                                                 constructor parameters if desired.
         * @property {boolean} alternativeContextMemory    true if the alternative Toolstrip tab selection behavior should be
         *                                                 used by the ContextManager. Currently used by Simulink Desktop.
         * @property {boolean} automaticallyShowWindow     true if the containing window should automatically be made visible and
         *                                                 brought to front upon container construction.
         * @property {Object}  borderOptions               Specifies the collective behavior of the set of children on each border (optional).
         *                                                 See ArrangeableBorderContainer borderOptions for more details.
         * @property {boolean} cleanStart                  true if information saved by the previous session should be ignored.
         * @property {Array}   contexts                    An array containing objects that define contexts that may be activated
         *                                                 independently of any document.  Typically, these may be modal contexts.
         *                                                 Each context may have associated toolstrip tab groups as well as associated panels.
         *                                                 Each object has the form
         *                                                 {
         *                                                     contextId: String,
         *                                                     tabGroupTags: [],
         *                                                     statusComponentTags: []
         *                                                     panelIds: [],
         *                                                 }, where all but the contextId properties are optional.
         *                                                 These contexts are activated by setting the UIContainer's activeContexts property.
         * @property {array}   cssFiles                    The strings in this array identify CSS files that should be dynamically loaded
         *                                                 for this container or any child container spawned by undocking
         * @property {Layout | string} defaultLayout       an object or JSON formatted string dictating the initial placement of children if
         *                                                 a layout is not recovered from the previous session.
         * @property {boolean} enableCenterCollapse        true if the center area, containing documents, should collapse when reduced
         *                                                 below its minimum size.
         * @property {boolean} enableOnTopExpand           true if it should be possible to expand panel collections that are
         *                                                 collapsed on the left or right on top of other content
         * @property {boolean} enableQABCustomization      true if Quick Access Bar customization should be allowed
         * @property {boolean} enableRemoteControl         true if the server should by able remotely control the UI
         * @property {boolean} lazyToolstripConstruction   true if toolstrip tab content should be constructed lazily when possible
         * @property {boolean} enableUsageOverlay          true if usage frequency information should be overlaid on the UI if it becomes available.
         *                                                 product and scope property values must also be specified for the information to appear.
         * @property {boolean} footerSpansFullWidth        true if the footer should span the entire width of the container rather than only the
         *                                                 width of the center component.  Note that a status bar is just a specific type of footer.
         * @property {boolean} groupDocumentsByType        true if tabs associated with a given type of documents should be kept together
         * @property {boolean} hasMultipleProductsOrScopes true if elements within the container may be associated with more than one product and/or
         *                                                 scope for the purposes of usage logging.
         * @property {boolean} hasToolstrip                true if the container should add a toolstrip at the top
         * @property {boolean} hasMnemonics                true if mnemonics and keyboard navigation should be available
         * @property {boolean} isDockable                  true if this container should be allowed to dock in its parent container
         * @property {number}  minorVersion                a layout, recovered from a previous session, will only be applied
         *                                                 if the minorVersion number stored in the layout matches this number.
         * @property {boolean} offerDocumentMaximizeButton true if a "maximize" button should appear in the upper right of documents
         *                                                 false if is should not. By default a maximize button will appear conditionally,
         *                                                 when there is no tab corresponding to the document (see showSingleDocumentTab).
         * @property {boolean} persistDocuments            true if the container should save and restore open documents across sessions.
         *                                                 Note that only documents for which a factory has been provided can be restored.
         * @property {boolean} persistLayout               true if the container should save and restore its layout across sessions
         * @property {string} product                      Identifies the product with which the UI is associate for usage logging purposes
         * @property {boolean} reserveDocumentSpace        true if empty space should be reserved for documents when none are open
         * @property {string} documentGroupingFacet        identified the property of documents that is used to group them.  By default
         *                                                 documents are grouped by "documentType".
         * @property {string} scope                        Identifies the scope with which the UI is associate for usage logging purposes
         * @property {SessionDataService} sessionDataService the service used by the container to save and recover its layout.
         *                                                 The UIContainer creates a sessionDataService by default but one may be
         *                                                 provided via the constructor parameters if desired.
         * @property {boolean} showSingleDocumentTab       true if a tab should appear when only one document is open
         * @property {Toolstrip} toolstrip                 the toolstrip displayed at the top of the container.  The UIContainer creates the
         *                                                 toolstrip.  There is no option for providing a client created toolstrip.
         * @property {Object}  qabControlToActionMap       maps quick access toolbar control tags to corresponding place holder action ids
         * @property {UIBuilder} uiBuilder                 the builder used to construct the container toolstrip and its content.
         *                                                 The UIContainer creates a uiBuilder by default but one may be
         *                                                 provided via the constructor parameters if desired.
         * @property {string} undockedPage                 The html page within which undocked containers should appear.  If no undockedPage
         *                                                 is specified a default will be used.
         * @property {Array} prefixesOfElementIdsToRedact  An array strings, containing prefixes of element ids whose content (beyond the
         *                                                 prefix) should be redacted when logging usage
         * @property {boolean} userDocumentTilingEnabled   true if the user should be able to adjust the document area tiling
         * @instance
         */
        Fixed: undefined,  // Trick to make the JSDoc generator list the above under "Fixed"

        // Default values for some of the above fixed properties
        minorVersion: 1,
        hasToolstrip: true,
        hasMnemonics: false,
        enableQABCustomization: false,
        enableRemoteControl: true,
        documentGroupingFacet: DocumentTypeProperties.FACET,
        showSingleDocumentTab: true,
        enableOnTopExpand: true,
        enableUsageOverlay: true,
        mainContainerTraversalOrder : ["top", "left", "center", "bottom", "right"],
        automaticallyShowWindow: true,
        // Would like the following to be true by default but this causes tFixedPointTool_main_destroy to fail
        // TODO: investigate failure and reinstate
        // enableCenterCollapse: true,

        // Dynamic Properties
        /**
         * @property {Array} activeContexts             The strings within the array identify "contexts" that are not associated with any
         *                                              document but drive the appearance of additional toolstrip tab groups and/or additional panels.
         * @property {number} bottomHeight              Reflects/controls height of the bottom panel collection.
         *                                              If >= 1 pixels are assumed. If < 1 interpreted as a fraction of the container height.
         * @property {array} columnWeights              Reflects/controls the relative widths of document grid columns
         * @property {DOCUMENT_LAYOUT | string}
         *                       documentLayout         Reflects/Controls the arrangement of documents.  This is a subset of the layout property.
         * @property {_Widget} footer                   A widget to be displayed at the bottom of the UI
         * @property {{w: number, h: number}} gridSize  Reflects/controls the document layout grid dimensions
         * @property {boolean} isBottomCollapsed        Reflects/controls the collapsed state of the bottom panel collection
         * @property {boolean} isBusy                   Reflects/controls whether the container is in a "busy" state, indicated by
         *                                              displaying an overlay and spinner
         * @property {boolean} isLeftCollapsed          Reflects/controls the collapsed state of the left panel collection
         * @property {boolean} isRightCollapsed         Reflects/controls the collapsed state of the right panel collection
         * @property {boolean} isToolstripCollapsed     Reflects/controls the collapsed state of the toolstrip.  This property does
         *                                              not distinguish the temporary expanded-on-top state.  This state is considered a
         *                                              sub-state of collapsed.
         * @property {boolean} isVisible                Reflects/controls the visibility of the containing window.    This property
         *                                              is supported when the UIContainer is in a CEF window but not when in a browser window.
         * @property {_Widget} lastSelectedDocument     Reports the document that was most recently selected among those that are currently
         *                                              open and docked.  This is the document whose context is active.  The value will be
         *                                              falsey if no document is open and docked.
         * @property {Layout | string}  layout          Reflects/controls the placement of panels and documents
         * @property {number} leftWidth                 Reflects/controls width of the left panel collection.
         *                                              If >= 1 pixels are assumed. If < 1 interpreted as a fraction of the container width.
         * @property {PanelLayout | string} panelLayout Reflects/controls the arrangement of panels along the borders of the UI.
         *                                              This is subset of the layout property.  Refer to Property_Object_Definitions below
         *                                              for PanelLayout object details.
         * @property (string) placeHolderText           Text that will appear in the area where documents normally appear when none are open
         * @property {number} rightWidth                Reflects/controls width of the right panel collection.
         *                                              If >= 1 pixels are assumed. If < 1 interpreted as a fraction of the container width.
         * @property {array} rowWeights                 Reflects/controls the relative heights of document grid rows
         * @property {_Widget | string} selectedChild   Reflects/controls the selected child.  The property can be set to a string corresponding
         *                                              to the child's panelId or title.  The get will always return the child widget.
         * @property {{title: string, tag: string}}
         *                    selectedToolstripTab      Reflects/controls the selected toolstrip tab.  When setting, either the title or tag will suffice.
         * @property {string} tabPosition               Reflects/controls the position of the document tabs.  Permissible values are
         *                                              "left", "right", "top" and "bottom.
         * @property {array} tileCoverage               Reflects/controls the relationship between document tiles and the underlying grid.
         *                                              Specifically an array of arrays identifying the tile associated with each grid
         *                                              cell in row major order.  For example [0  0; 1 2] specifies three tiles on a 2 x 2 grid with
         *                                              the first tile spanning two columns.
         * @property {string}  title                    displayed as the page or window title
         * @property {{x: number, y: number, w: number, h: number}}
         *                   windowBounds               Reflects/controls the position and size of the containing window.  This property
         *                                              is supported when the UIContainer is in a CEF window but not when in a browser window.
         * @instance
         */
        Dynamic: undefined,  // Trick to make the JSDoc generator list the above under "Dynamic"

        // The following is more easily viewed in the JSDoc.
        /**
         * The following definitions define the contents of a number of object "types" referred to in the above property definitions.<br/><br/>
         *
         * <table style="margin-left:3em">
         * Layout := {<br/>
         *     <tr><td>majorVersion:</td> <td>{number},</td></tr>
         *     <tr><td>minorVersion:</td> <td>{number},</td></tr>
         *     <tr><td>panelLayout:</td> <td>{PanelLayout},</td></tr>
         *     <tr><td>documentLayout:</td> <td>{DocumentLayout},</td> <td><i>optional</i></td></tr>
         *     <tr><td>windowBounds:</td> <td>{Bounds},</td> <td><i>optional</i></td></tr>
         * </table>
         * }<br/><br/>
         *
         * PanelLayout := {<br/>
         * <table style="margin-left:3em">
         *     <tr><td>referenceWidth:</td> <td>{number},</td> <td><i>internal</i></td></tr>
         *     <tr><td>referenceHeight:</td> <td>{number},</td> <td><i>internal</i></td></tr>
         *     <tr><td>centerExpanded:</td> <td>{boolean},</td> <td><i>optional</i></td></tr>
         *     <tr><td>left:</td> <td>{PanelContainer},</td> <td><i>optional</i></td></tr>
         *     <tr><td>right:</td> <td>{PanelContainer},</td> <td><i>optional</i></td></tr>
         *     <tr><td>bottom:</td> <td>{PanelContainer}</td> <td><i>optional</i></td></tr>
         * </table>
         * }<br/><br/>
         *
         * PanelContainer := {<br/>
         * <table style="margin-left:3em">
         *     <tr><td>freeDimension:</td> <td>{number},</td> <td><i>internal</i></td></tr>
         *     <tr><td>collapsed:</td> <td>{boolean},</td> <td><i>optional</i></td></tr>
         *     <tr><td>portion:</td> <td>{number},</td> <td><i>optional</i></td></tr>
         *     <tr><td>children:</td> <td>[Panel, ...]</td></tr>
         * </table>
         * }<br/><br/>
         *
         * Panel := {<br/>
         * <table style="margin-left:3em">
         *     <tr><td>id: {string},</td></tr>
         *     <tr><td>collapsed: {boolean},</td> <td><i>optional</i></td></tr>
         *     <tr><td>portion: {number}</td> <td><i>optional</i></td></tr>
         * </table>
         * }<br/><br/>

         * DocumentLayout := {<br/>
         * <table style="margin-left:3em">
         *     <tr><td>gridDimensions: {Dimensions},</td></tr>
         *     <tr><td>tileCount: {number},</td></tr>
         *     <tr><td>columnLeft: [{number}, ...],</td> <td><i>internal</i></td></tr>
         *     <tr><td>columnWeights: [{number}, ...],</td> <td><i>optional</i></td></tr>
         *     <tr><td>rowTop: [{number}, ...],</td> <td><i>internal</i></td></tr>
         *     <tr><td>rowWeights: [{number}, ...],</td> <td><i>optional</i></td></tr>
         *     <tr><td>referenceWidth: {number},</td> <td><i>internal</i></td></tr>
         *     <tr><td>referenceHeight: {number},</td> <td><i>internal</i></td></tr>
         *     <tr><td>tileCoverage: [[{number}, ...], ...],</td> <td><i>optional</i></td></tr>
         *     <tr><td>tileOccupancy: [{TileOccupants}, ...],</td> <td><i>optional</i></td></tr>
         *     <tr><td>nestings: [{DocumentLayout}, ...],</td> <td><i>optional</i></td></tr>
         *     <tr><td>nestingId: {string}</td> <td><i>for nestings only</i></td></tr>
         * </table>
         * }<br/><br/>
         *
         * TileOccupants := {<br/>
         * <table style="margin-left:3em">
         *     <tr><td>children: [{DocumentOrNesting}, ...],</td></tr>
         *     <tr><td>showingChildId: {string}</td> <td><i>optional</i></td></tr>
         * </table>
         * }<br/><br/>
         *
         * DocumentOrNesting := {<br/>
         * <table style="margin-left:3em">
         *      <tr><td>id: {string},</td></tr>
         *      <tr><td>showOrder: {number},</td> <td><i>internal</i></td></tr>
         *      <tr><td>selectionOrder: number}</td> <td><i>internal</i></td></tr>
         * </table>
         * }<br/><br/>

         * Bounds := {<br/>
         * <table style="margin-left:3em">
         *      <tr><td>x: {number},</td></tr>
         *      <tr><td>y: {number},</td></tr>
         *      <tr><td>w: {number},</td></tr>
         *      <tr><td>h: {number}</td></tr>
         * </table>
         * }<br/><br/>

         * Dimensions := {<br/>
         * <table style="margin-left:3em">
         *      <tr><td>w: {number},</td></tr>
         *      <tr><td>h: {number}</td></tr>
         * </table>
         * }<br/><br/>
         *
         * <i>internal</i> - denotes properties that are typically not specified by clients but rather are set
         *                   by the UIContainer when it persists its layout
         * @instance
         */
        Property_Object_Definitions: undefined,

        // Properties that should generally not be accessed by sub-classes
        // _layoutKey
        // _focusManager
        // _selectionManager
        // _contextManager
        // _contextDriver
        // _outerContainer
        // _mainContainer
        // _groupRegistry
        // _propertyDelegates
        // _documentContainer
        // _documentContainerState
        // _documentContainerPropertyDelegator
        // _documentContainerActions
        // _isDocumentContainerExpanded
        // _panels - stores {properties, widget, watches} for each, keyed by panelId
        // _documents - stores {properties, widget, watches) for each in array
        // _toolBars - stores {properties, widget, watches} for each tool bar in array
        // _childProperties - stores properties keyed by widget.id
        // _waitingForLayout
        // _restoringLayout

        /**
         * Constructor
         *
         * @param id {string} unique string (among UIContainers) identifying the UI.  The id is used to refer to the container's
         *             session state and global context.
         * @param properties {Object} containing optional properties as described above.  Not required.
         * @instance
         */
        constructor: function (id, properties) {
            var toolstripWrapper, parentAppId;
            var that = this;
            declare.safeMixin(this, properties);
            this.appId = this.appId || id;
            this.id = id;
            this.toolstripTag = id + "Toolstrip";
            this._layoutKey = this.appId + "Layout";
            this._documentsKey = this.appId + "Documents";
            this._dockedContainers = {};
            this._panels = {};
            this._documents = [];
            this._toolBars = [];
            this._closeApprovers = [];
            this._childProperties = {};
            this._groupRegistry = this.parentContainer ? this.parentContainer._groupRegistry : new DocumentGroupRegistry();
            this._documentTypes = [];  // Subset of registered types in container
            this._panelSetTitles = {global: this.globalPanelSetTitle};
            this._propertyDelegates = {};
            this._pendingTypeProperties = {};

            delete this.Fixed;   // Not a real property just a JSDoc trick
            delete this.Dynamic; // same
            delete this.Property_Object_Definitions;  // same

            if (this.product && this.scope) {
                UsageLogger.start(this.product, this.scope, this.hasMultipleProductsOrScopes);
                if (this.prefixesOfElementIdsToRedact) {
                    UsageLogger.setPrefixesOfElementIdsToRedact(this.prefixesOfElementIdsToRedact, this.product, this.scope);
                }
            }

            DockingManager.setContainer(this);

            if (this.persistDocuments === undefined) {
                this.persistDocuments = this.persistLayout;
            }
            if (this.isDocked) {
                // Set references to parent services
                this.sessionDataService = this.parentContainer.sessionDataService;
                this.actionService = this.parentContainer.actionService;
                this.uiBuilder = this.parentContainer.uiBuilder;

                // TODO: Are the following needed?
                this._selectionManger = this.parentContainer._selectionManager;
                this._contextManager = this.parentContainer._contextManager;
                this._contextDriver = this.parentContainer._contextDriver;
                this._focusManager = this.parentContainer._focusManager;
                this.parentContainer._dockedContainers[id] = this;
            } else {
                var rootId = this.parentContainer ? "uiroot" : id;
                this._outerContainer = new BorderContainer({id : rootId, uiContainer: this}, rootId);
                // Prevent some children from being rendered prematurely (before container startup) (see geck 1455655)
                domClass.add(this._outerContainer.domNode, "hideAllChildren");
                this._mainContainer = new ArrangeableBorderContainer({id : id + "Main",
                                                          enableOnTopExpand: this.enableOnTopExpand,
                                                          borderOptions: this.borderOptions,
                                                          supportFooter: !this.footerSpansFullWidth,
                                                          panelSetTitles: this._panelSetTitles,
                                                          traversalOrder: this.mainContainerTraversalOrder,
                                                          minimumCenterWidth: 200});
                this._mainContainer.on(LayoutContainerEvent.STATE_CHANGED, lang.hitch(this, "_mainContainerStateChanged"));
                this._outerContainer.addWithProperties(this._mainContainer, lang.mixin(new Stateful(), {region : "center"}));
                if (this.title) {
                    this.set("title", this.title);
                }
                WindowUtils.addWindowBoundsListener(window, function (bounds, moreExpected) {
                    that._changeAttrValue(WINDOW_BOUNDS, bounds);
                    if (!moreExpected && !that.parentContainer) {
                        // TODO: Should this be skipped for the browser case?
                        that._handleLayoutChange();
                    }
                });

                if (window.cefclient) {
                    window.cefclient.handleWindowClosed = function () {
                        that.close();
                    };
                    window.cefclient.sendMessage("js-custom-close-set");
                } else {
                    if (!this.parentContainer) {
                        on(window, "beforeunload", function () {
                            that.close(true);  // Force close because there is no ability to make async queries at this point.
                        });
                    }
                }

                if (!this.parentContainer) {
                    window.name = id;
                }

                // Construct the session data service
                if (!this.sessionDataService) {
                    this.sessionDataService = new LocalSessionDataService();
                    this.sessionDataService.setMinimumSaveInterval(this._layoutKey, 1000);
                }
                ChildWindowManager.setSessionDataService(this.sessionDataService);

                // Construct the action service, toolstrip and context manager
                if (!this.actionService) {
                    this.actionService = new ActionDataService({
                        nameSpace: "/actions/main"
                    });
                }

                if (!this.uiBuilder) {
                    this.uiBuilder = UIBuilderFactory.getInstance({
                        actionService: this.actionService,
                        enableQABCustomization: this.enableQABCustomization,
                        lazyLoading: this.lazyToolstripConstruction
                    });
                }

                this._selectionManager = this._mainContainer.selectionManager;

                if (this.hasToolstrip) {
                    // Construct the toolstrip widget and add it to the outer container
                    this._toolstripId = this.uiBuilder.create({
                        tag: this.toolstripTag,
                        type: TypeConstants.TOOLSTRIP
                    });
                    this.toolstrip = registry.byId(this._toolstripId);
                    if (properties.showTopLeftButtonInToolstrip) {
                        this.toolstrip.addTopLeftButton();
                    }
                    this.uiBuilder.addPropertySetCallback(this._toolstripId, lang.hitch(this, this._handleToolstripPropertyChange));
                    // Monitoring the "selectedTab" property change using the above misses change event that result from the
                    // addition of new tabs.  Therefore we listen to the property on the toolstrip itself.
                    this.toolstrip.watch("selectedTab", lang.hitch(this, this._handleToolstripSelectedTabChange));

                    // The toolstrip needs to be placed in a wrapper because it manipulates its parent's (the wrapper's)
                    // height upon collapse and expand.
                    toolstripWrapper = new ContentPane({id : TOOLSTRIP_ID});
                    toolstripWrapper.set("content", this.toolstrip);
                    this._outerContainer.addWithProperties(toolstripWrapper, {region: "top"});
                    BorderBar.setCloseClickExclusion(toolstripWrapper.domNode);

                    if (this.parentContainer && this.isDockable !== false) {
                        // Add a redock button to the toolstrip
                         var dockButton = new PushButton({
                             'class': "dockButton",
                             icon: "smallDock",
                             showText: false,
                             description: this.title ? lang.replace(layoutL10n.dockSomething, {placeholder: this.title})
                                                     : layoutL10n.dock
                         }).placeAt(this.toolstrip.topRightContainer);
                        dockButton.on('click', lang.hitch(this, "_dock"));
                    }

                    parentAppId = (this.parentContainer && this.contentType !== DockingManager.CHILD_UI_CONTENT) ? this.parentContainer.id : id;
                    this._contextManager = ContextManagerFactory.getInstance({
                        uiContainer: this,
                        uiBuilder: this.uiBuilder,
                        parentAppId: parentAppId,
                        toolstripTag: this.toolstripTag,
                        qabContextualControlsActionMap: this.qabControlToActionMap || {},
                        alternativeContextMemory: properties.alternativeContextMemory
                    });

                    // g1678616: Set this property to ensure that we do not add onFocus listener on the toolstrip. This will guarantee that
                    // selected panel/document is not getting deselected when we click on toolstrip drop down button or tab.
                    toolstripWrapper.dontSelectOnFocus = true;

                    this._childProperties[TOOLSTRIP_ID] = toolstripWrapper;
                    this._selectionManager.addChild(toolstripWrapper, toolstripWrapper);

                    /* Uncomment to log context changes
                    this._contextManager.watch("currentContext", function (name, oldValue, newValue) {
                        Log.info("Context change from: " + JSON.stringify(oldValue));
                        Log.info(" to: " + JSON.stringify(newValue));
                    });
                    */
                }

                var IEVersion = utils.detectIE();
                if (this.hasMnemonics && (IEVersion === false || IEVersion >= 13)) {
                    if (!this.hasToolstrip) {
                        throw new Error("'hasMnemonics' cannot be true without 'hasToolstrip' also being true!");
                    }

                    this._mnemonicsManager = new MnemonicsManager({
                        toolstripId: this._toolstripId
                    });
                }

                this._focusManager = new FocusManager(this.toolstrip);

                this._propertyDelegates[ArrangeableBorderContainer.IS_LEFT_COLLAPSED] = this._mainContainer;
                this._propertyDelegates[ArrangeableBorderContainer.IS_RIGHT_COLLAPSED] = this._mainContainer;
                this._propertyDelegates[ArrangeableBorderContainer.IS_BOTTOM_COLLAPSED] = this._mainContainer;
                this._propertyDelegates[ArrangeableBorderContainer.LEFT_WIDTH] = this._mainContainer;
                this._propertyDelegates[ArrangeableBorderContainer.RIGHT_WIDTH] = this._mainContainer;
                this._propertyDelegates[ArrangeableBorderContainer.BOTTOM_HEIGHT] = this._mainContainer;
                this._propertyDelegates[SelectionManager.SELECTED_CHILD] = this._selectionManager;

                // Need to construct context driver after property delegates have been set so that it can watch the selected child
                if (this._contextManager) {
                    this._contextDriver = new ContextDriver(parentAppId, this);
                }

                this._documentContainerPropertyDelegator = this._createDocumentContainerPropertyDelegator(properties);
                this._documentContainerActions = new DocumentContainerActions(this.actionService);
                if (this.reserveDocumentSpace) {
                    this._addDocumentContainer();
                }

                this._propertyDelegates[DocumentContainer.TAB_POSITION] = this._documentContainerPropertyDelegator;
                this._propertyDelegates[DocumentContainer.ARRANGEMENT] = this._documentContainerPropertyDelegator;
                this._propertyDelegates[DocumentContainer.GRID_SIZE] = this._documentContainerPropertyDelegator;
                this._propertyDelegates[DocumentContainer.ROW_WEIGHTS] = this._documentContainerPropertyDelegator;
                this._propertyDelegates[DocumentContainer.COLUMN_WEIGHTS] = this._documentContainerPropertyDelegator;
                this._propertyDelegates[DocumentContainer.TILE_COVERAGE] = this._documentContainerPropertyDelegator;
                this._propertyDelegates[DocumentContainer.PLACE_HOLDER_TEXT] = this._documentContainerPropertyDelegator;

                if (this.defaultLayout && typeof(this.defaultLayout) === "string") {
                    this.defaultLayout = JSON.parse(this.defaultLayout);

                }
                this._initializeLayout();
                this._mainContainer.on(LayoutContainerEvent.STATE_CHANGED, function () {
                    if (!that._isDestroying) {
                        that._handlePanelLayoutChange();
                        that._handleLayoutChange();
                    }
                });
            }

            // Instantiate the key bindings
            this._keyBindings = KeyBindingsFactory.getInstance(this.actionService);
        },

        startup: function () {
            if (this._started) {
                return;
            }
            var that = this;
            this.emit(STARTING);
            this._started = true;
            if (!this.isDocked) {
                if (this._contextManager) {
                    if (this.tabGroupTags) {
                        this._contextManager.register("TabGroups", {
                            appId: this.id,
                            contextId: "global",
                            tabGroupTags: this.tabGroupTags
                        });
                    }
                    if (this.contexts) {
                        // Register tab groups, status components and panels for each context
                        this.contexts.forEach(function (entry) {
                            that._registerContextualElements(that.id, entry.contextId, entry);
                        });
                    }
                }

                domClass.remove(this._outerContainer.domNode, "hideAllChildren");
                this._outerContainer.startup();
                if (!window.cefclient) {
                    // Push setting WINDOW_BOUNDS to the end of the queue to allow resize to complete
                    setTimeout(() => {
                        this._changeAttrValue(WINDOW_BOUNDS, WindowUtils.getWindowBounds(window));
                    }, 0)
                }
            }
            this._restoreDocuments();
            // If this is an undocked container, select the first showing document
            if (!this.isDocked && this.parentContainer) {
                var i;
                for (i = 0; i < this._documents.length; i++) {
                    if (this._documents[i].properties.get(ChildProperties.IS_SHOWING)) {
                        this._documents[i].properties.set(ChildProperties.IS_SELECTED, true);
                        break;
                    }
                }
            }
            if (this.product && this.scope) {
                if (this.toolstrip) {
                    UsageLogger.enableLoggingFromNode(this.toolstrip.domNode, "toolstrip");
                }
                UsageLogger.logEvent({
                    eventType: "opened",
                    elementId: this.appId,
                    elementType: "app"
                });
                if (this.enableUsageOverlay && MessageService.isRunning()) {
                    UsageOverlay.enable(this.product, this.scope);
                }
                this.watch(SelectionManager.SELECTED_CHILD, function(name, oldValue, newValue) {
                    var id;
                    if (newValue) {
                        var properties = that.getChildProperties(newValue);
                        id = properties.documentType || properties.panelId;
                        if (properties.documentId) {
                            id += ":" + properties.documentId;
                        }
                    }
                    UsageLogger.setSelectedContext(id);
                });
            }
            this.emit(STARTED);
            if (this.enableRemoteControl) {
                UIContainerPeer.enableAttach(this);
            }

            // Create the key listener using KeyBindings API
            this._createKeyBindings();
        },

        /**
         * @returns {_Widget} the top level _Widget associated with the container
         * @instance
         */
        getRootNode: function () {
            return this._outerContainer.domNode;
        },

        /**
         * For a standalone container: saves state (provide save is enabled) and then destroys content.
         * For a container that is docked inside another: Removes this container's content from the parent container
         *
         * @instance
         */
        shutdown: function () {
            var that = this;
            var properties;
            this.emit(STOPPING);
            UsageLogger.stop();
            if (this._peer) {
                this._peer.destroy();
                delete this._peer;
            }
            if (this.isDocked) {
                // Remove panels, documents and types from parent
                Object.keys(this._panels).forEach(function (panelId) {
                    properties = that._panels[panelId].properties;
                    properties.isContextual = properties.saveIsContextual;
                    that.parentContainer.removePanel(panelId);
                });

                if (this.persistDocuments) {
                    this._saveDocuments();
                }

                this._getDocumentTypes().forEach(function (type){
                    that.parentContainer._documentContainer.removeGroup(type);
                    properties = that._getPropertiesForType(type);
                    properties.tabGroupTags = properties.saveTabGroupTags;
                    properties.panelIds = properties.savePanelIds;
                    that.parentContainer.unregisterDocumentType(type);
                });

                if (this._actionIds) {
                    this._actionIds.forEach(function (id) {
                        that.actionService.removeAction(id);
                    });
                }
                if (this._toolstripComponentTags) {
                    this._toolstripComponentTags.forEach(function (tag) {
                        var id = that.uiBuilder.tagToId(tag);
                        if (id) {
                            that.uiBuilder.destroy(id);
                        }
                    });
                }

                delete this.parentContainer._dockedContainers[this.id];
                this.emit(STOPPED);
                return;
            }

            // Save state and cleanup
            if (this.persistLayout) {
                this._saveLayout();
            }
            if (this.persistDocuments) {
                this._saveDocuments();
            }
            this.set(FOOTER, null);
            this.destroyRecursive();
            this.emit(STOPPED);
        },

        /**
         * Adds a panel to the left, right or bottom.  The panel is assumed to be a Dojo widget.
         * A number of properties affect the container's display of the panel.
         * These properties may be supplied either as part of the panel object or via the second,
         * optional "properties" parameter.  If a properties object is supplied it should be
         * an instance of ChildProperties.  The advantages of supplying a separate properties object
         * include 1) avoidance of name conflicts between widget internal properties and container related
         * properties and 2) ability to monitor container related properties in isolation.  See the
         * ChildProperties class definition for details regarding the supported properties.
         *
         * When a panel is added its placement will be determined by one of the following, in priority order:
         *
         * <ol>
         *    <li>The previous session layout</li>
         *    <li>The default layout</li>
         *    <li>Region property supplied</li>
         * </ol>
         *
         * To avoid any focus issues, it is preferred to have a focus method implemented for the widget.
         *
         * @param widget {_Widget} widget to be displayed
         * @param properties {ChildProperties | Object} properties associated with panel (optional)
         * @param factory {UIComponentFactory} used to produce widget and properties when needed, if not supplied
         * @param state {Object} information to be given to factory when producing widget/properties
         *
         * @instance
         */
        addPanel: function (widget, properties, factory, state) {
            if (factory && !properties) {
                properties = factory.createProperties(this, state);
            }
            var panelInfo = {
                widget: widget,
                properties: properties || widget,
                factory: factory,
                state: state
            };
            var panelId = panelInfo.properties.panelId;
            properties = panelInfo.properties;
            //Log.info("addPanel: " + panelId);
            var that = this;
            if (this.persistLayout) {
                this._assertPropertyDefined(panelInfo, "panelId");
            }
            if (this._panels[panelId]) {
                return;  // already added
            }
            if (properties.isMaximizable === undefined) {
                properties.isMaximizable = true;
            }
            Log.assert(properties.region !== "center", "Attempting to place panel: " + properties.title + " in center.  Only documents can appear there");
            this._panels[panelId] = panelInfo;
            if (this.isDocked) {
                properties.saveIsContextual = properties.isContextual;
                properties.isContextual = true;
                this.parentContainer.addPanel(widget, properties, factory, state)
            } else {
                if (properties.qabControlToActionMap && this._contextManager) {
                    Object.keys(properties.qabControlToActionMap).forEach(function (controlTag) {
                        that._contextManager.register("QuickAccessAction", {
                            appId: that.id,
                            contextId: properties.panelId,
                            quickAccessControlTag: controlTag,
                            actionId: properties.qabControlToActionMap[controlTag]
                        });
                    });
                }

                if (panelInfo.properties.isContextual) {
                    panelInfo.properties.isOpen = false;
                } else if (!this._waitingForLayout) {
                    this._addPanelToLayout(panelInfo);
                }
                panelInfo.watches = [
                    panelInfo.properties.watch(ChildProperties.IS_OPEN, function (name, oldValue, newValue) {
                        if (!that._restoringLayout && newValue && oldValue === false) {
                            that._addPanelToLayout(panelInfo);
                        }
                    })
                ];
                if (!this.reserveDocumentSpace) {
                    // Watch for a panel being moved to the bottom, while there is an empty document container
                    // If this happens, remove the document container allowing the bottom panel to fill the space.
                    panelInfo.watches.push(panelInfo.properties.watch(ChildProperties.REGION,
                        function (name, oldValue, newValue) {
                            if (newValue === "bottom" && that._documentContainer && that._documentContainer.isEmpty()) {
                                that._removeDocumentContainer();
                            }
                        })
                    );
                }
            }
            this.emit(LayoutContainerEvent.CHILD_ADDED, {
                childType: "panel",
                widget: panelInfo.widget,
                properties: panelInfo.properties
            });
        },

        /**
         * Removes a panel.
         *
         * @param idTitleOrWidget {_Widget | string} this may be a string identifying the child or the child widget itself.
         *                        If it is a string it may either be the panelId or the title.
         * @return the removed panel widget
         * @instance
         */
        removePanel: function (idTitleOrWidget) {
            //Log.info("removePanel: " + idTitleOrWidget);
            var that = this;
            var properties = this.getChildProperties(idTitleOrWidget);
            var widget;
            var panelInfo;
            if (properties) {
                properties.isRemoving = true;
                if (this.isDocked) {
                    delete this._panels[properties.panelId];
                    widget = this.parentContainer.removePanel(idTitleOrWidget);
                } else {
                    properties.set(ChildProperties.IS_OPEN, false);
                    panelInfo = this._panels[properties.panelId];
                    if (panelInfo.widget) {
                        delete this._childProperties[panelInfo.widget.id];
                        widget = panelInfo.widget;
                    }
                    delete this._panels[properties.panelId];
                    panelInfo.watches.forEach(function (watch) {
                        watch.remove();
                    });

                    if (properties.qabControlToActionMap && this._contextManager) {
                        Object.keys(properties.qabControlToActionMap).forEach(function (controlTag) {
                            that._contextManager.unregister("QuickAccessAction", {
                                appId: properties.panelId,
                                contextId: "global",
                                quickAccessControlTag: controlTag
                            });
                        });
                    }
                    this.emit(LayoutContainerEvent.CHILD_REMOVED, {
                        childType: "panel",
                        widget: panelInfo.widget,
                        properties: panelInfo.properties
                    });
                }
                properties.isRemoving = false;
                return widget;
            }
        },

        /**
         * Registers a document type.  A document type corresponds to a collection of documents which are treated similarly
         * by the UIContainer.  In particular, documents of the same type typically share contextual toolstrip tabs.
         * The supplied properties tell the UI container how to display and manage documents of the given type.
         * It should be an instance of DocumentTypeProperties.  This isn't strictly required but facilitates making and
         * tracking property changes where permissible.  See the DocumentTypeProperties class definition for
         * details regarding the supported properties.
         *
         * A type must be registered before any documents of that type are added via addDocument.
         *
         * @param type {string} that uniquely identifies the type, not displayed
         * @param properties {DocumentTypeProperties | Object} properties associate with the type
         * @instance
         */
        registerDocumentType: function (type, properties) {
            var that = this;
            if (properties.tabGroupTags === undefined) {
                properties.tabGroupTags = [];
            }
            if (properties.plusActions) {
                this._convertStringsToActions(properties.plusActions);
            }
            this.registerDocumentGroup(DocumentTypeProperties.FACET, type, properties, false);
            this._addDocumentType(type);
            // Some documents from a previous session may be waiting for their type to be registered before they can be restored.
            this._restoreDocuments();
            if (this.parentContainer && this.contentType === type) {
                // Listen for changes to the type's title and reflect them on the container title
                this._outerContainer.own(properties.watch(TITLE, function(name, oldValue, newValue) {
                    that.set(TITLE, newValue);
                }));
            }
        },

        /**
         * Causes the container to forget about the specified document type
         *
         * @param type {string} identifies the type
         * @instance
         */
        unregisterDocumentType: function (type) {
            if (this.isDocked) {
                this.parentContainer.unregisterDocumentType(type);
                return;
            }
            this._removeDocumentType(type);
            this._groupRegistry.unregister(DocumentTypeProperties.FACET, type);
        },

        /**
         * Registers a document group.  A document group is a set of documents that share some common properties and
         * can be managed as a unit by the UI container.  A document group is similar to a document type in some ways
         * but differs in others.  Both identify a set of documents that can be managed as a unit but a document type
         * can also be associated with various contextual UI elements whereas a group cannot.
         *
         * A group must be registered before any documents belonging it are added via addDocument
         *
         * @param facet {string} the facet of the document which determines the group being registered.  More specifically
         *                       this is the object key within the document's properties under which the group id is stored.
         * @param id {string} identifies the group
         * @param properties {DocumentGroupProperties) the group properties to be stored
         * @param autoUnregister {boolean} true if the group should automatically be unregistered when the last member
         *                                 document is closed (true by default)
         * @instance
         */
        registerDocumentGroup: function (facet, id, properties, autoUnregister) {
            properties._autoUnregister = autoUnregister !== false;
            if (properties.get(DocumentGroupProperties.DOCUMENT_COUNT) === undefined) {
                properties.set(DocumentGroupProperties.DOCUMENT_COUNT, 0);
            }
            this._groupRegistry.register(facet, id, properties);
        },

        /**
         * Causes the container to forget about the specified document group.  This call is unnecessary if autoUnregister
         * was specified when the group was registered.
         *
         * @param facet {string} the facet associated with the group
         * @param id {string} the group identifier.
         * @instance
         */
        unregisterDocumentGroup: function (facet, id) {
            this._groupRegistry.unregister(facet, id);
        },

        /**
         * Adds a document to the container.  The document is assumed to be a Dojo widget.
         * A number of properties affect the container's display of the document.
         * These properties may be supplied either as part of the document object or via the second,
         * optional "properties" parameter.  If a properties parameter is supplied it should be
         * an instance of ChildProperties.  The advantages of supplying a separate properties object
         * include 1) avoidance of name conflicts between widget internal properties and container related
         * properties 2) ability to monitor container related properties in isolation and 3) the ability to
         * track document properties across dock/undock (docking and undocking destroy and recreate
         * the widget).  See the ChildProperties class definition for details regarding the supported properties.
         *
         * The widget argument can be null.  However, in this case the registered properties associated with the
         * specified documentType must include a factory.
         *
         * To avoid any focus issues, it is preferred to have a focus method implemented for the widget.
         *
         * @param widget {_Widget} Dojo widget to be displayed
         * @param properties {ChildProperties } Object} properties associated with document (optional)
         * @param  state {object} state to be passed to the document factory's createWidget method if the
         *               widget hasn't already been created.
         * @instance
         */
        addDocument: function (widget, properties, state) {
            properties = properties || widget;
            var documentInfo = {widget: widget, properties: properties, state: state};
            //Log.info("addDocument: " + documentInfo.properties.title);
            var that = this;
            if (this.isDocked) {
                properties.owningContainer = this;
                this._documents.push(documentInfo);
                this.parentContainer.addDocument(widget, properties, state);
                return;
            }

            this._assertPropertyDefined(documentInfo, "documentType");
            this._assertPropertyDefined(documentInfo, "title");
            var typeProperties = this._groupRegistry.getGroupPropertiesFromDocumentProperties(DocumentTypeProperties.FACET, properties);
            Log.assert(typeProperties, "DocumentTypeProperties haven't been registered for " + documentInfo.properties.documentType);

            if (this._addPhantomDocument(widget, properties, typeProperties, state)) {
                return;
            }

            documentInfo.factory = typeProperties.documentFactory;
            this._documents.push(documentInfo);

            if (!this.parentContainer) {
                if (!properties.isDocking) {
                    this._logDocumentOpen(properties, true);
                }

                var undocked;
                if (!typeProperties.get(DocumentTypeProperties.IS_DOCKED)) {
                    undocked = this._addDocumentUndocked(documentInfo, properties.documentType);
                } else if (!properties.isDocking && typeProperties.isUndockable() && properties.get(DocumentTypeProperties.IS_DOCKED) === false) {
                    undocked = this._addDocumentUndocked(documentInfo);
                }
                if (undocked || properties.isDockable === false) {
                    this._documents.pop();
                    if (widget) {
                        // The widget will be recreated in the undocked container
                        widget.destroyRecursive();
                    }
                    return;
                }
            }

            if (!widget) {
                widget = this._createDocumentWidget(properties, typeProperties, state);
                documentInfo.widget = widget;
            }
            this._childProperties[widget.id] = properties;

            var isDocked = !this.parentContainer || this.contentType === properties.documentType;
            // Remove !this.parentContainer restriction to support multi-level undock
            if (!this.parentContainer && typeProperties.isUndockable()) {
                properties.setForWindow(window, "_undock", lang.hitch(this, "_undockDocument"));
            }
            if (!(properties.isUndocking || properties.isDocking)) {
                this._adjustDocumentCounts(properties, 1);
            }
            if (properties.actions) {
                this._convertStringsToActions(properties.actions);
            }
            this._addDocumentToLayout(documentInfo);
            properties.set(ChildProperties.IS_DOCKED, isDocked);
            if (documentInfo.factory) {
                this._saveDocuments();
            }
            properties.watch(ChildProperties.IS_OPEN, function(name, oldValue, newValue) {
                if (!newValue && !that._isDestroying) {
                    if (oldValue) {
                        that._logDocumentOpen(properties, false);
                    }
                    CleanupManager.cleanup(properties);
                    that._saveDocuments();
                }
            });
            properties.__dockWatch = properties.watch(ChildProperties.IS_DOCKED, function(name, oldValue, newValue) {
                if (newValue === oldValue) {
                    return;
                }
                if (newValue) {
                    if (!isDocked) {
                        that._dock();
                    }
                    if (properties.__undockedWindow) {
                        delete properties.__undockedWindow;
                    }
                } else if (isDocked) {
                    that._undockDocument(widget);
                }
            });
            if (properties instanceof ChildProperties) {
                // Set the origin property of the docmument before handling WINDOW_BOUNDS
                // Setting "origin" will prevent typeProperties from being able to override local changes, so use 'origin_inherit'
                // alias to allow ChildProperties to decide how to handle the set
                properties.set('origin_inherit', (properties.origin || typeProperties.origin || ChildProperties.TOP_LEFT_ORIGIN));
                
                typeProperties.__originWatch = typeProperties.watch(ChildProperties.ORIGIN, function (name, oldValue, newValue) {
                    properties.set('origin_inherit', (typeProperties.origin || ChildProperties.TOP_LEFT_ORIGIN));
                });
                
                // Before adding the watch on WINDOW_BOUNDS, set the initial value of WINDOW_BOUNDS if a ChildProperties object
                // Set of "windowBounds" may be locked, so use property alias to force the set
                properties.set("windowBounds_force", WindowUtils.getWindowBounds(properties.__undockedWindow || window, properties.origin));
            }
            properties.__boundsWatch = properties.watch(ChildProperties.WINDOW_BOUNDS, function (name, oldValue, newValue) {
                if (!properties.__respondingToBoundsChange && properties.__undockedWindow) {
                    WindowUtils.applyDefaultBounds(newValue, true);
                    WindowUtils.convertBoundsToPixels(newValue);
                    WindowUtils.setWindowBounds(properties.__undockedWindow, newValue, properties.origin);
                }
            });
            if (this.parentContainer && this.contentType === DockingManager.SINGLE_DOCUMENT_CONTENT) {
                // Listen for changes to the document's title and reflect them on the container title
                this._outerContainer.own(properties.watch(TITLE, function(name, oldValue, newValue) {
                    that.set(TITLE, newValue);
                }));
            }

            this.emit(LayoutContainerEvent.CHILD_ADDED, {
                childType: "document",
                widget: documentInfo.widget,
                properties: documentInfo.properties
            });
            properties.set("isUndocking", false);  // May have been set true upon undock
            properties.set("isDocking", false);  // May have been set true upon dock
        },

        /**
         * Closes a document.  The document widget will automatically be destroyed unless the corresponding
         * destroyOnClose property has been set false.
         *
         * @param idTitleOrWidget {_Widget | string} this may be a string identifying the child or the child widget itself.
         *                        If it is a string it may either be the documentId or the title.
         * @instance
         */
        closeDocument: function (idTitleOrWidget) {
            var properties = this.getChildProperties(idTitleOrWidget);
            if (properties) {
                properties.set(ChildProperties.IS_OPEN, false);
            }
        },

        /**
         * This method passes through to the container's ActionDataService.  It should be called rather than calling
         * ActionDataService.loadActions directly.  Doing so allows the UIContainer to keep track of actions
         * associated with a child container and remove them when the container is closed or undocked.
         *
         * @param actions {array} contains action objects
         * @instance
         */
        loadActions: function (actions) {
            var that = this;
            this.actionService.loadActions(actions);
            if (this.isDocked) {
                if (!this._actionIds) {
                    this._actionIds = [];
                }
                actions.forEach(function (action) {
                    that._actionIds.push(action.id);
                });
            }
        },

        /**
         * This method adds a tab group to the container.  The tab group may be specified by providing a
         * config, and array of configs or a tag.  When an array of configs is provided the first config is
         * assumed to describe the tabs and subsequent configs are assumed to describe associated popups.
         * If configs are provided, this method it will call the appropriate UIBuilder methods to build the
         * tabs and popups.  If instead you call the UIBuilder methods directly, you still need to call
         * addTabGroup to make the container aware of the tabs.

         * @param configsOrTag {object | array | string} - defines or identifies the tab group (see above for details)
         * @param isContextual {boolean} true if the tab group is contextual, i.e. should only appear when
         *                               a certain context or contexts is activated.
         */
        addTabGroup: function (configsOrTag, isContextual) {
            var tag;
            if (typeof(configsOrTag) === "string") {
                tag = configsOrTag;
                this._saveToolstripComponentTag(tag);
            } else if (Array.isArray(configsOrTag)) {
                if (configsOrTag.length > 1) {
                    for (i = 1; i < configsOrTag.length; i++) {
                        this.createPopupsFromConfig(configsOrTag[i]);
                    }
                }
                tag = configsOrTag[0].tag;
                this.addTabGroupFromConfig(configsOrTag[0], tag);
            } else {
                tag = configsOrTag.tag;
                this.addTabGroupFromConfig(configsOrTag, tag);
            }
            if (!isContextual) {
                // Register global tabs with context manager
                // Contextual tabs will be registered when the context itself is registered
                if (!this.tabGroupTags) {
                    this.tabGroupTags = [tag];
                } else {
                    this.tabGroupTags.push(tag);
                }
                this._contextManager.register("TabGroups", {
                    appId: this.id,
                    contextId: "global",
                    tabGroupTags: this.tabGroupTags
                });
            }
        },

        /**
         * Adds a toolbar below the toolstrip.  Currently only a single toolbar is supported but in the future multiple
         * toolbars will be supported.
         *
         * @param widget {_Widget} widget to be displayed
         * @param properties {ChildProperties | Object} properties associated with the toolbar (optional)
         * @param properties.title {string} - Used to refer to the toolbar on show/hide menu
         * @param properties.isShowing - controls/reflects the visibility of the toolbar
         * @param factory {UIComponentFactory} used to produce the toolbar widget and properties lazily if they are not supplied (optional)
         * @instance
         */
        addToolBar: function (widget, properties, factory) {
            var that = this;

            // For now remove the current tool bar if any
            if (this._toolBars.length > 0) {
                this.removeToolBar(this._toolBars[0].widget);
            }
            if (factory && !properties) {
                properties = factory.createProperties(this);
            }
            if (factory && !widget) {
                widget = factory.createWidget(this, properties);
            }
            if (!properties) {
                properties = widget;
            }

            var setShowing = function (showing) {
                if (showing) {
                    domStyle.set(widget.domNode, "display", "block");
                    domStyle.set(widget.domNode, "height", "27px");
                } else {
                    domStyle.set(widget.domNode, "display", "none");
                    domStyle.set(widget.domNode, "height", "0");
                }
            };
            setShowing(properties.isShowing !== false);

            widget.domNode.setAttribute("role", "toolbar region");
            if (properties.ariaLabel) {
                widget.domNode.setAttribute("aria-label", properties.ariaLabel);
            }

            properties.region = "top";
            properties.layoutOrder = -1;
            this._mainContainer.addWithProperties(widget, properties);

            var watches = [];
            watches[0] = properties.watch(ChildProperties.IS_SHOWING, function (name, oldValue, newValue) {
                setShowing(newValue);
                that._mainContainer.layout();
            });
            this._toolBars[0] = {
                widget: widget,
                properties: properties,
                watches: watches
            };

            this._childProperties[widget.id] = widget;
            this._selectionManager.addChild(widget, properties);
        },

        /**
         * Removes a previously added toolbar
         *
         * @param titleOrWidget {_Widget | string} this may be either the toolbar's title or the toolbar widget itself
         * @instance
         */
        removeToolBar: function (titleOrWidget) {
            if (this._toolBars.length > 0 && (this._toolBars[0].widget === titleOrWidget || this._toolBars[0].properties.title === titleOrWidget)) {
                this._mainContainer.removeChild(this._toolBars[0].widget);
                this._toolBars[0].watches[0].remove();
                this._toolBars.length = 0;
            }
        },

        /**
         * This method adds a status bar to the container.  The status bar may be specified by providing a
         * config, a tag or a status bar object.  If a config is provided the UIBuilder will be called to
         * construct the status bar.  Similarly if a tag is provided it will be used to obtain an already
         * specified status bar from the UIBuilder.

         * @param configTagOrBar {object | string | StatusBar} - defines, identifies or provides the status bar (see above for details)
         */
        addStatusBar: function (configTagOrBar) {
            var statusBar;
            if (typeof(configTagOrBar) === "string") {
                this.statusBarId = this.uiBuilder.tagToId(configTagOrBar);
                statusBar = registry.byId(this.statusBarId);
            } else if (configTagOrBar.isInstanceOf && configTagOrBar.isInstanceOf(StatusBar)) {
                statusBar = configTagOrBar;
            } else {
                this.uiBuilder.addStatusBarFromConfig(configTagOrBar, configTagOrBar.tag);
                this.statusBarId = this.uiBuilder.tagToId(configTagOrBar.tag);
                statusBar = this.uiBuilder.widgetByTag(configTagOrBar.tag);
            }
            statusBar.domNode.style.height = "22px";  // This probably shouldn't be necessary since the height is
                                                      // already specified via CSS.  Instead BorderContainer should
                                                      // figure it out from the computed style.
            this.set(FOOTER, statusBar);
        },

        /**
         * Adds a function that will be consulted before closing the container's enclosing window along with the container
         * itself.  In order for close to proceed all approvers must assent.  Approvers will be consulted when the window is
         * a Chromium Embedded Frame (CEF) but not when it is a browser tab.  The browser environment does not provide a
         * sufficient API for implementing a controlled close veto.
         *
         * @param approver {function} a function that returns either a boolean or a Promise.  If a boolean is returned it
         * should be true if close may proceed or false if close should be halted.  If a promise is returned it should
         * either be resolved to approve close or rejected to veto it.
         */
        addCloseApprover: function (approver) {
            this._closeApprovers.push(approver);
        },

        /**
         * Removes a previously added close approver
         */
        removeCloseApprover: function (approver) {
            var index = this._closeApprovers.indexOf(approver);
            if (index >= 0) {
                this._closeApprovers.splice(index, 1);
            }
        },

        /**
         * Gets the ultimate ancestor of this container.  This may be the container itself or in the case of an undocked
         * container its parent or higher ancestor.
         *
         * @returns {UIContainer}
         */
        getRootUIContainer: function () {
            var container = this;
            while (container.parentContainer) {
                container = container.parentContainer;
            }
            return container;
        },

        /**
         * Returns an array containing the widget and properties associated with each panel that has been added to the container.
         *
         * @param region optional argument that limits the result to panels occupying the specified border region
         * @returns {[{widget: _Widget, properties: Object]} array containing the widget and properties associated with each panel
         * @instance
         */
        getPanels: function (region) {
            var that = this;
            var panels = [];
            Object.keys(this._panels).forEach(function (panelId) {
                var panelInfo = that._panels[panelId];
                if (!region || panelInfo.properties.region === region) {
                    panels.push({widget: panelInfo.widget, properties: panelInfo.properties});
                }
            });
            return panels;
        },

        /**
         * Returns an array containing the widget and properties associated with each document of the specified type
         * that is currently open in the container.  If a type is not specified then information for all document will
         * be returned.
         *
         * @param type the type of documents of interest (optional)
         * @returns {[{widget: _Widget, properties: Object]} array containing the widget and properties associated with each document
         * @instance
         */
        getDocuments: function (type) {
            var documents = [];
            this._documents.forEach(function (documentInfo) {
                if (!type || type === documentInfo.properties.documentType) {
                    documents.push({widget: documentInfo.widget, properties: documentInfo.properties});
                }
            });
            return documents;
        },

        /**
         * @param idTitleOrWidget this may be a string identifying the child or the child widget itself.
         * @param type optionally indicates the type of document to avoid potential ambiguity when requesting properties
         *             for a document identified by documentId or title.
         * @returns {Object} the properties associated with a child (panel or document)
         * @instance
         */
        getChildProperties: function (idTitleOrWidget, type) {
            if (typeof(idTitleOrWidget) === "string") {
                var info = this._getChildInfo(idTitleOrWidget, type);
                if (info) {
                    return info.properties;
                }
            }
            // Assume the parameter is a widget
            return this._childProperties[idTitleOrWidget.id];
        },

        /**
         * @param idTitleOrProperties this may be a string identifying the child or the child properties object
         * @param type optionally indicates the type of document to avoid potential ambiguity when requesting the widget
         *             for a document identified by documentId or title.
         * @returns {_Widget} the widget associated with a child (panel or document)
         * @instance
         */
        getChildWidget: function (idTitleOrProperties, type) {
            var info = this._getChildInfo(idTitleOrProperties, type);
            return info ? info.widget : null
        },

        /**
         * Returns an array containing the type id and properties associated with each registered document type.
         *
         * @returns {[{type: string, properties: Object]} array containing the type id and properties associated with each type
         * @instance
         */
        getDocumentTypes: function () {
            var that = this;
            var types = [];
            this._getDocumentTypes().forEach(function(type) {
                types.push({type: type, properties: that._getPropertiesForType(type)});
            });
            return types;
        },

        /**
         * @param type {string} the document type whose properties are sought
         * @returns {Object} the properties associated with the specified document type
         * @instance
         */
        getDocumentTypeProperties: function (type) {
            return this._getPropertiesForType(type);
        },

        /**
         * Assigns the container ownership of a connection so that it will be removed upon destruction of the container.
         * @param connection the connection
         * @instance
         */
        own: function (connection) {
            this._outerContainer.own(connection);
        },

        /**
         * Destroys the container and all of its content.  Generally this method should not be called directly.
         * The shutdown method should be called instead.
         *
         * @instance
         */
        destroyRecursive: function () {
            var that = this;
            this._isDestroying = true; // Suppress layout save

            if (this._documentContainer) {
                this._documentContainer.clear();
            }

            if (this._mnemonicsManager) {
                this._mnemonicsManager.destroy();
            }

            if (this._contextManager) {
                ContextManagerFactory.destroy();
            }

            if (this.uiBuilder) {
                UIBuilderFactory.destroyInstance();
            }

            if(this._focusManager) {
                this._focusManager.destroy();
            }

            Object.keys(this._panels).forEach(function (panelId) {
                var widget = that._panels[panelId].widget;
                that.removePanel(panelId);
                if (widget) {
                    widget.destroyRecursive();
                }
            });
            this._getRegisteredDocumentTypes().forEach(function (type) {
                if (that.parentContainer) {
                    that._removeDocumentType(type);
                } else {
                    that.unregisterDocumentType(type);
                }
            });
            this._outerContainer.destroyRecursive();
            this.actionService.destroy();
            if (this._keyBindings) {
                KeyBindingsFactory.destroyInstance();
            }
            this._isDestroyed = true;
        },

        /**
         * Sets the layout back to the last saved layout or the default
         * @instance
         */
        resetLayout: function () {
            this._initializeLayout();
        },

        /**
         * Creates a child of this UIContainer.  A UIContainer must be created in this manner if it is to be
         * dockable within this, the parent, container.
         *
         * @param id {string} uniquely identifies the container
         * @param factory {UIContainerFactory} factory that produces an instance of the container
         * @param dock {boolean} true if the container should open docked
         * @instance
         */
        createChildUI: function (id, factory, dock) {
            if (dock === undefined) {
                dock = DockingManager.shouldDockUI(id);
            }
            // Create a packing list
            var packingList = DockingManager.createPackingList(this);
            packingList.containerFactoryPath = factory.modulePath;
            packingList.cssFiles = factory.getCSSFiles();
            packingList.parent = this;

            if (dock) {
                packingList.isDocked = true;
                DockingManager.buildContainer(UIContainer, packingList);
            } else {
                DockingManager.spawnContainer(this, factory.getPage() || this.undockedPage, DockingManager.CHILD_UI_CONTENT, packingList);
            }
        },

        isChildContainer: function () {
            return !!this.parentContainer;
        },

        /**
         * @param id {string} the child container identifier
         * @returns {UIContainer} the requested child container instance
         * @instance
         */
        getChildUIContainer: function (id) {
            var childContainer = this._dockedContainers[id];
            return childContainer && !childContainer._isDestroyed ? childContainer : null;
        },

        /**
         * @returns {string} the root selector for UIContainer Keyboard Navigation
         */
        getRootSelector: function () {
            return ROOT_SELECTOR;
        },

        _getChildInfo: function (reference, type) {
            var info, i;
            if (!type) {
                // Since a type wasn't specified we may be looking for a panel
                if (typeof(reference) === "string") {
                    // Assume the parameter is a panel id
                    info = this._panels[reference];
                    if (info) {
                        return info;
                    }
                }
                // Apparently it wasn't a panelId. Assume it is a title or widget
                for (i = 0; i < this._panels.length; i++) {
                    info = this._panels[i];
                    if (info.properties.title === reference || info.widget === reference || info.properties === reference) {
                        return info;
                    }
                }
            }
            for (i = 0; i < this._documents.length; i++) {
                info = this._documents[i];
                if ((info.properties.title === reference || info.properties.documentId === reference ||
                     info.widget.id === reference || info.widget === reference || info.properties === reference) &&
                    (!type || info.properties.documentType === type)) {
                    return info;
                }
            }
            return null;
        },

        _getDocumentInfo: function (widget) {
            var i, info;
            for (i = 0; i < this._documents.length; i++) {
                info = this._documents[i];
                if (info.widget === widget) {
                    return info;
                }
            }
            return null;
        },

        _getPropertiesForType: function (type) {
            return this._groupRegistry.getProperties(DocumentTypeProperties.FACET, type);
        },

        _getDocumentTypes: function () {
            return this._documentTypes;
        },

        _getRegisteredDocumentTypes: function () {
            return this._groupRegistry.getIds(DocumentTypeProperties.FACET);
        },

        _adjustDocumentCounts: function (documentProperties, change) {
            var facet = this.documentGroupingFacet;
            var groupId = documentProperties[facet];
            this._adjustCountsRecursive(documentProperties, facet, change);
            if (groupId) {
                var groupProperties = this._groupRegistry.getProperties(facet, groupId);
                var isOpen = groupProperties.documentCount > 0 ||
                    (this._documentContainer && this._documentContainer.hasChildContainer(groupId));
                if (isOpen !== groupProperties.get(DocumentGroupProperties.IS_OPEN)) {
                    groupProperties.set(DocumentGroupProperties.IS_OPEN, isOpen);
                }
            }
        },

        _adjustCountsRecursive: function (documentProperties, facet, change) {
            var groupId = documentProperties[facet];
            if (groupId) {
                var groupProperties = this._groupRegistry.getProperties(facet, groupId);
                groupProperties.set(DocumentGroupProperties.DOCUMENT_COUNT, groupProperties.get(DocumentGroupProperties.DOCUMENT_COUNT) + change);
                if (groupProperties._autoUnregister && groupProperties.get(DocumentGroupProperties.DOCUMENT_COUNT) === 0) {
                    this._groupRegistry.unregister(facet, groupId);
                }
                if (groupProperties.subGroupingFacet) {
                    this._adjustCountsRecursive(documentProperties, groupProperties.subGroupingFacet, change);
                }
            }
        },

        /**
         * @deprecated call addTabGroup instead
         */
        createPopupsFromConfig: function (config) {
            var that = this;
            this.uiBuilder.createPopupsFromConfig(config);
            config.popups.forEach(function (popup) {
                that._saveToolstripComponentTag(popup.tag);
            });
        },

        /**
         * @deprecated call addTabGroup instead
         */
        addTabGroupFromConfig: function (config, tag) {
            this.uiBuilder.addTabGroupFromConfig(config, tag);
            this._saveToolstripComponentTag(tag);
        },

        _saveToolstripComponentTag: function (tag) {
            if (this.isDocked) {
                if (!this._toolstripComponentTags) {
                    this._toolstripComponentTags = [];
                }
                this._toolstripComponentTags.push(tag);
            }
        },

        _addPanelToLayout: function (panelInfo) {
            // Place panels on the left by default
            var properties = panelInfo.properties;
            properties.region = properties.region || "left";

            if (!panelInfo.widget) {
                panelInfo.widget = panelInfo.factory.createWidget(this, panelInfo.state);
            }

            // The following is done after the createWidget call above because the actions may not be loaded into the
            // action service until createWidget is called.
            if (properties.actions) {
                this._convertStringsToActions(properties.actions);
            }

            this._childProperties[panelInfo.widget.id] = panelInfo.properties;
            if (panelInfo.properties !== panelInfo.widget) {
                this._mainContainer.addWithProperties(panelInfo.widget, panelInfo.properties);
            } else {
                this._mainContainer.addChild(panelInfo.widget);
            }
            properties.set(ChildProperties.IS_OPEN, true);
        },

        _addDocumentType: function (type) {
            var that = this;
            if (this._documentTypes.indexOf(type) >= 0) {
                return;  // Already added
            }
            this._documentTypes.push(type);

            var properties = this._getPropertiesForType(type);
            var pending = this._pendingTypeProperties[type];
            if (pending && pending.windowBounds) {
                properties.windowBounds = pending.windowBounds;
            }
            if (this.isDocked) {
                // Add the container's global tabs and panels to the type properties
                properties.saveTabGroupTags = properties.tabGroupTags;
                if (this.tabGroupTags) {
                    properties.tabGroupTags = properties.tabGroupTags || [];
                    properties.tabGroupTags = this.tabGroupTags.concat(properties.tabGroupTags);
                }
                properties.savePanelIds = properties.panelIds;
                var panelIds = Object.keys(this._panels);
                if (panelIds.length > 0) {
                    properties.panelIds = panelIds.concat(properties.panelIds);
                }
                properties.owningContainerId = this.id;
                this.parentContainer._addDocumentType(type);
                return;
            }
            this._panelSetTitles[type] = properties.title;
            this._registerContextualElements(type, "global", properties);
            if (properties.contexts && this._contextManager) {
                properties.contexts.forEach(function (entry) {
                    this._registerContextualElements(type, entry.contextId, entry);
                }, this);
            }
            if (properties.qabControlToActionMap && this._contextManager) {
                Object.keys(properties.qabControlToActionMap).forEach(function (controlTag) {
                    that._contextManager.register("QuickAccessAction", {
                        appId: type,
                        contextId: "global",
                        quickAccessControlTag: controlTag,
                        actionId: properties.qabControlToActionMap[controlTag]
                    });
                });
            }
            if (!DockingManager.isUndockedWindow()  && properties.setForWindow) {
                if (!this.parentContainer && properties.owningContainerId && properties.owningContainerId !== this.id) {
                    properties.setForWindow(window, "_undock", lang.hitch(this, "_undockChildUI", properties.owningContainerId));
                }
                else if (!this.parentContainer && properties.isUndockable()) {
                    properties.setForWindow(window, "_undock", lang.hitch(this, "_undockType", type));
                }
            }
            if (!this.parentContainer && properties.tabGroupFactories) {
                // Use the factories to construct any tabs that haven't already been constructed
                for (var tag in properties.tabGroupFactories) {
                    if (!this.uiBuilder.tagToId(tag)) {
                        properties.tabGroupFactories[tag].createWidget(this, null, properties);
                    }
                }
            }
            if (!this.parentContainer && properties.statusComponentFactory) {
                properties.statusComponentFactory.createWidget(this, null, properties);
            }

            var isDocked = properties.get(DocumentTypeProperties.IS_DOCKED);
            properties.__dockWatch = properties.watch(DocumentTypeProperties.IS_DOCKED, function(name, oldValue, newValue) {
                if (newValue === oldValue) {
                    return;
                }
                if (newValue) {
                    if (!isDocked && that.parentContainer) {
                        that._dock();
                    }
                } else if (isDocked) {
                    that._undockType(type);
                }
            });

            properties.__boundsWatch = properties.watch(ChildProperties.WINDOW_BOUNDS, function (name, oldValue, newValue) {
                var undockedWindow = DockingManager._getWindowForType(type);
                if (undockedWindow && !properties.__respondingToBoundsChange) {
                    WindowUtils.applyDefaultBounds(newValue, true);
                    WindowUtils.convertBoundsToPixels(newValue);
                    WindowUtils.setWindowBounds(undockedWindow, newValue);
                }
            });

            this.emit(LayoutContainerEvent.CHILD_ADDED, {
                childType: "documentType",
                typeId: type,
                properties: properties
            });
        },

        _addPhantomDocument: function (widget, properties, typeProperties, state) {
            var that = this;
            if (!properties.__phantomWatch) {
                properties.__phantomWatch = properties.watch(ChildProperties.IS_PHANTOM, function(name, oldValue, newValue) {
                    if (newValue === oldValue) {
                        return;
                    }
                    if (newValue) {
                        // Close the document without destroying it
                        var saveDestroy = properties.get(ChildProperties.DESTROY_ON_CLOSE);
                        properties.set(ChildProperties.DESTROY_ON_CLOSE, false);
                        properties.set(ChildProperties.IS_OPEN, false);
                        properties.set(ChildProperties.DESTROY_ON_CLOSE, saveDestroy);
                    } else {
                        that.addDocument(widget, properties, state);
                    }
                });
            }
            return properties.isPhantom;
        },

        _createDocumentWidget: function (properties, typeProperties, state) {
            Log.assert(typeProperties.documentFactory, "Must supply documentFactory if widget not supplied");
            CssLoader.loadFiles(typeProperties.documentFactory.getCssFiles());
            return typeProperties.documentFactory.createWidget(this, state, properties);
        },

        _registerContextualElements: function (appId, contextId, contextProperties) {
            if (!this._contextManager) {
                return;
            }
            if (contextProperties.tabGroupTags) {
                this._contextManager.register("TabGroups", {appId: appId, contextId: contextId, tabGroupTags: contextProperties.tabGroupTags});
            }
            if (contextProperties.statusComponentTags) {
                this._contextManager.register("StatusComponents", {appId: appId, contextId: contextId, statusComponentTags: contextProperties.statusComponentTags});
            }
            if (contextProperties.panelIds) {
                this._contextManager.register("Panels", {appId: appId, contextId: contextId, panelIds: contextProperties.panelIds});
            }
        },

        _removeDocumentType: function (type) {
            var that = this;
            var typeIndex = this._documentTypes.indexOf(type);
            if (typeIndex < 0) {
                return;
            }
           this._documentTypes.splice(typeIndex, 1);

            var properties = this._getPropertiesForType(type);
            delete this._panelSetTitles[type];
            if (properties) {
                if (properties.tabGroupTags && this._contextManager) {
                    this._contextManager.unregister("TabGroups", {appId: type, contextId: "global"});
                }
                if (properties.qabControlToActionMap && this._contextManager) {
                    Object.keys(properties.qabControlToActionMap).forEach(function (controlTag) {
                        that._contextManager.unregister("QuickAccessAction", {appId: type, contextId: "global", quickAccessControlTag: controlTag});
                    });
                }
                if (properties.contexts && this._contextManager) {
                    properties.contexts.forEach(function (entry) {
                        if (entry.tabGroupTags) {
                            that._contextManager.unregister("TabGroups", {appId: type, contextId: entry.contextId});
                        }
                        if (entry.statusComponentTags) {
                            that._contextManager.unregister("StatusComponents", {appId: type, contextId: entry.contextId});
                        }
                        if (entry.panelIds) {
                            that._contextManager.unregister("Panels", {appId: type, contextId: entry.contextId});
                        }
                    }, this);
                }
                if (properties.__dockWatch) {
                    properties.__dockWatch.remove();
                    delete properties.__dockWatch;
                }
                if (properties.__originWatch) {
                    properties.__originWatch.remove();
                    delete properties.__originWatch;
                }
                if (properties.__boundsWatch) {
                    properties.__boundsWatch.remove();
                    delete properties.__boundsWatch;
                }
                if (properties.__undockedWindow) {
                    delete properties.__undockedWindow;
                }
                this.emit(LayoutContainerEvent.CHILD_REMOVED, {
                    childType: "documentType",
                    typeId: type,
                    properties: properties
                });
            }
        },

        _addDocumentToLayout: function (documentInfo) {
            if (!this._documentContainer) {
                this._addDocumentContainer();
            }
            if (documentInfo.properties !== documentInfo.widget) {
                this._documentContainer.addWithProperties(documentInfo.widget, documentInfo.properties);
            } else {
                this._documentContainer.addChild(documentInfo.widget);
            }
            documentInfo.properties.set(ChildProperties.IS_OPEN, true);
        },

        _addToLayout: function (panelId, setId) {
            var info = this._panels[panelId];
            if (info) {
                if (!info.properties.setId) {
                    info.properties.setId = setId;
                }
                info.properties.set(ChildProperties.IS_OPEN, true);
            }
        },

        _removeFromLayout: function (panelId) {
            var info = this._panels[panelId];
            if (info) {
                info.properties.set(ChildProperties.IS_OPEN, false);
            }
        },

        _getContextualState: function (setId) {
            return this._mainContainer.getState(setId);
        },

        _setContextualState: function (state, setId) {
            this._mainContainer.setState(state, setId, true /* isContextual */);
        },

        getOccupiedBorders: function () {
            return this._mainContainer.getOccupiedBorders();
        },

        _assertPropertyDefined: function (childInfo, property) {
            Log.assert(childInfo.properties[property], property + " not specified");
        },

        _convertStringsToActions: function (actions) {
            var i, action, actionObject;
            for (i = 0; i < actions.length; i++) {
                action = actions[i];
                if ("string" === typeof action) {
                    actionObject = this.actionService.getAction(action);
                    if (actionObject) {
                        actions[i] = this._createLazyAction(action);
                    }
                } else if (action instanceof Array) {
                    this._convertStringsToActions(action);
                }
            }
        },

        _createLazyAction: function (actionId) {
            // actionService.getAction returns a snap shot of action properties, not a live action.
            // This method creates a wrapper action that retrieves the properties on-the-fly.
            // This avoids having to make internal layout containers such as DocumentContainer aware of
            // the action service.
            var actionService = this.actionService;
            var action = this.actionService.getAction(actionId);
            return {
                text: action.text.replace("\n", " "),  // Will appear in tooltip and therefore should not be multi-line
                description: action.description,
                itemType: action.itemType,
                getEnabled: function () {return actionService.getAction(actionId).enabled;},
                getSelected: function () {return actionService.getAction(actionId).selected;},
                setEnabled: function (value) {actionService.updateAction(actionId, {enabled: value});},
                setSelected: function (value) {actionService.updateAction(actionId, {selected: value});},
                callback: function (e) {
                    // Get the callback from the action service associated with the invoking window
                    if (e && e.view) {
                        e.view.DockingManager.getContainer().actionService.getAction(actionId).callback(arguments);
                    } else {
                        actionService.getAction(actionId).callback(arguments);
                    }
                }
            };
        },

        _addDocumentContainer: function () {
            if (this._documentContainer) {
                return;
            }
            var that = this;
            var containerProperties = {
                id: this.id + "DocumentContainer",
                groupRegistry: this._groupRegistry,
                groupingFacet: this.documentGroupingFacet,
                showSingleDocumentTab: this.showSingleDocumentTab,
                offerMaximizeButton: this.offerDocumentMaximizeButton,
                userTilingEnabled: this.userDocumentTilingEnabled !== false,
                groupTabsByType: this.groupDocumentsByType,
                selectionManager: this._selectionManager
            };
            // The container.setState call below will only set tab positions within individual tiles.
            // The following specifies the default for tiles without a specified individual tab position.
            if (this._documentContainerState && this._documentContainerState.tabPosition) {
                containerProperties.tabPosition = this._documentContainerState.tabPosition;
            }
            if (this._documentContainerState && this._documentContainerState.placeHolderText) {
                containerProperties.placeHolderText = this._documentContainerState.placeHolderText;
            }
            var container = new DocumentContainer(containerProperties);
            if (this._documentContainerState) {
                container.setState(this._documentContainerState);
                delete this._documentContainerState;
            }
            var containerProperties = {
                region : "center"
            };
            if (this.enableCenterCollapse) {
                containerProperties.getCollapsedWidget = function (region) {
                    var tabPosition = container.get(DocumentContainer.TAB_POSITION);
                    var bar = new DocumentBar({
                        id: container.id + "Bar", documentContainer: container, region: region,
                        buttonOrientation: tabPosition === "left" || tabPosition === "right" ? "horizontal" : "vertical"
                    });
                    bar.on(LayoutContainerEvent.EXPAND_ALL, function () {
                        that._mainContainer.restore("center", region);
                    });
                    return bar;
                };
                containerProperties.getCollapsedSize = function () {
                    var size = {w: 24, h: 24};
                    var tabPosition = container.get(DocumentContainer.TAB_POSITION);
                    if (tabPosition === "left" || tabPosition === "right") {
                        // TODO: Get current document bar width, but clip to min center size (= 200).
                        size.w = 110;
                    }
                    return size;
                };
            }
            this._mainContainer.addWithProperties(container, lang.mixin(new Stateful(), containerProperties));
            this._documentContainer = container;
            this._documentContainerActions.setContainer(container);
            if (this._contextDriver) {
                // The document container must be provided to the context driver before the UIContainer adds its own
                // CHILD_ADDED, CHILD_REMVOED listeners because the context driver needs to respond first to these events.
                this._contextDriver.setDocumentContainer(container);
            }
            this._documentContainerPropertyDelegator.setDocumentContainer(container);

            container.on(LayoutContainerEvent.CHILD_ADDED, function (document, properties) {
                // If the document is selected or if no other document is selected
                if (properties.get(ChildProperties.IS_SELECTED) || !that.lastSelectedDocument ||
                    !dom.isDescendant(that.lastSelectedDocument.domNode, container.domNode) ||
                    !container.getSelectedDocument()) {
                    that.set(LAST_SELECTED_DOCUMENT, document);
                }
                properties.uiContainerSelectWatch = properties.watch(ChildProperties.IS_SELECTED, function (name, oldValue, newValue) {
                    if (newValue) {
                        that.set(LAST_SELECTED_DOCUMENT, document);
                    }
                });
                properties.uiContainerShowWatch = properties.watch(ChildProperties.IS_SHOWING, function (name, oldValue, newValue) {
                    if (!newValue && document === that.lastSelectedDocument &&
                        !container.isRestoringState() && !container.isChangingTiling()) {
                        var documents = container.getShowingDocuments();
                        if (documents.length > 0) {
                            that.set(LAST_SELECTED_DOCUMENT, documents[0]);
                        }
                    }
                });
            });

            container.on(LayoutContainerEvent.CHILD_REMOVED, function (document, properties) {
                // Note that a document may have been remove because it was closed or because it was undocked
                var documentInfo = that._getDocumentInfo(document);
                var index = that._documents.indexOf(documentInfo);
                that._documents.splice(index, 1);
                delete that._childProperties[document.id];
                // If the document belongs to a child container remove it from the child's document list too
                if (documentInfo.properties.owningContainer && documentInfo.properties.owningContainer !== that) {
                    var childDocuments = documentInfo.properties.owningContainer._documents;
                    index = childDocuments.indexOf(documentInfo);
                    childDocuments.splice(index, 1);
                }
                if (!(properties.isUndocking || properties.isDocking)) {
                    that._adjustDocumentCounts(properties, -1);
                }

                properties.uiContainerSelectWatch.unwatch();
                properties.uiContainerShowWatch.unwatch();
                var documents = container.getShowingDocuments();
                if (documents.length > 0) {
                    that.set(LAST_SELECTED_DOCUMENT, documents[0]);
                } else {
                    that.set(LAST_SELECTED_DOCUMENT, null);
                }

                if (container.isEmpty() && !that._isDestroying && !that._isDocking) {
                    if (that.parentContainer && that.contentType !== DockingManager.CHILD_UI_CONTENT) {
                        // If this container is in an undocked window and the last document has been closed, close the window
                        window.close();
                    } else {
                        var currentSelection = that._selectionManager.get(SelectionManager.SELECTED_CHILD);
                        // Remove the document container if not reserving space for it and if there is a panel on
                        // the bottom that will expand to fill the space.
                        if (!that.reserveDocumentSpace && that._mainContainer.hasPanelInRegion("bottom")) {
                            that._removeDocumentContainer();
                        }
                        if (that._mainContainer.isExpanded("center")) {
                            that._mainContainer.restore("center");
                        }
                        if (currentSelection) {
                            // Removing the document container may have perturbed selection.  Restore it.
                            if (!that._documentContainer) {
                                setTimeout(function () {
                                    that._selectionManager.setSelected(currentSelection, true);
                                }, 0);
                            }
                        } else {
                            // Select a border panel
                            var panelContainer;
                            // If the document container was removed, select the bottom panel that will have expanded to
                            // fill its space.
                            if (!that._documentContainer) {
                                panelContainer = that._mainContainer.getChildInRegion("bottom");
                            }
                            // If the document container remains or a bottom panel is not present select a successor panel.
                            if (!panelContainer) {
                                panelContainer = that._mainContainer.nextChild(that._documentContainer, true);
                            }
                            if (panelContainer && panelContainer.nextChild) {
                                var successor = panelContainer.nextChild(null, true);
                                if (successor) {
                                    setTimeout(function () {
                                        that._selectionManager.setSelected(successor, true);
                                    }, 0);
                                }
                            }
                        }
                    }
                }
                that.emit(LayoutContainerEvent.CHILD_REMOVED, {
                    childType: "document",
                    widget: documentInfo.widget,
                    properties: documentInfo.properties
                });
            });
            container.on(LayoutContainerEvent.STATE_CHANGED, function () {
                that._handleDocumentLayoutChange();
                that._handleLayoutChange();
            });
            this._mainContainerStateChanged();
        },

        _removeDocumentContainer: function () {
            if (!this._documentContainer) {
                return;
            }
            this._documentContainer.clear();  // Removes tiling so that it won't be restored when container reopened.
            this._documentContainerState = this._documentContainer.getState();
            this._mainContainer.removeChild(this._documentContainer);
            this._documentContainer.destroyRecursive();
            delete this._documentContainer;
            this._documentContainerActions.setContainer(null);
            if (this._contextDriver) {
                this._contextDriver.setDocumentContainer(null);
            }
        },

        _mainContainerStateChanged: function () {
            if (this._documentContainer) {
                var isExpanded = this._mainContainer.isExpanded("center");
                if (isExpanded != this._isDocumentContainerExpanded) {
                    this._isDocumentContainerExpanded = isExpanded;
                    this._documentContainer.ancestorMaximizeUpdated(isExpanded);
                }
            }
        },

        _initializeLayout: function () {
            var that = this;
            if (this.persistLayout && !this.cleanStart) {
                // Retrieve and restore the layout
                this._waitingForLayout = true;
                this.sessionDataService.retrieve(this._layoutKey).then(
                    function (layout) {
                        that._restoreLayout(layout, true);
                        that._waitingForLayout = false;

                        // Now retrieve and restore documents
                        if (that.persistDocuments) {
                            that.sessionDataService.retrieve(that._documentsKey).then(
                                function (documents) {
                                    that._documentsToRestore = documents;
                                    that._documentToRestoreCount = documents.length;
                                    that._errorDuringDocumentRestore = false;
                                    that._restoreDocuments();
                                },
                                function () {
                                    // Error callback (no documents stored)
                                }
                            );
                        }
                        that._initialShow();
                    },
                    function () {
                        // Error callback
                        that._restoreLayout(that.defaultLayout);
                        that._initialShow();
                        that._waitingForLayout = false;
                    }
                );
            } else {
                if (this.defaultLayout) {
                    this._restoreLayout(this.defaultLayout);
                }
                this._initialShow();
            }
        },

        _initialShow: function() {
                if (this.automaticallyShowWindow && window.cefclient) {
                    if (this.windowBounds && !this._restoredBounds) {
                        this.set(WINDOW_BOUNDS, this.windowBounds);
                    }
                    WindowUtils.bringToFront(window);
                }
        },

        _saveLayout: function () {
            if (this.persistLayout && !this._restoringLayout && !this._waitingForLayout) {
                try {
                    // Use the layout getter rather than the Stateful.get method so that the
                    // "crossSession" = true can be passed.
                    this.sessionDataService.save(this._layoutKey, this._layoutGetter(true));
                } catch (e) {
                    // If the container state is in flux getState may error.
                    // Fail silently, since another save will be triggered as the state settles.
                }
            }
        },

        _restoreLayout: function (layout, restoringFromPreviousSession) {
            this._restoringLayout = true;
            var that = this;
            if (layout && this._canRestoreLayout(layout.majorVersion, layout.minorVersion)) {

                // Save panel scroll positions
                Object.keys(this._panels).forEach(function (panelId) {
                    var panelInfo = that._panels[panelId];
                    if (panelInfo.widget) {
                        LayoutUtils.saveScrollPosition(panelInfo.widget, panelInfo.properties);
                    }
                });

                if (layout.windowBounds) {
                    this.set(WINDOW_BOUNDS, layout.windowBounds);
                    this._restoredBounds = true;
                }

                if (this.hasToolstrip && layout.toolstripCollapsed != undefined) {
                    this.set(IS_TOOLSTRIP_COLLAPSED, layout.toolstripCollapsed);
                }
                
                if (restoringFromPreviousSession && layout.types) {
                    Object.keys(layout.types).forEach(function (type) {
                        var savedProperties = layout.types[type];
                        var properties = that._getPropertiesForType(type);
                        if (properties) {
                            if (savedProperties.windowBounds) {
                                properties.windowBounds = savedProperties.windowBounds;
                            }
                        } else {
                            // Type may not have been registered yet
                            that._pendingTypeProperties[type] = savedProperties;
                        }
                    });
                }

                if (this._contextManager) {
                    this._contextManager._panelManager.setContextualStates(layout.contexts);
                }
                var mainState = layout.panelLayout || layout.borders || layout.main;
                this._mainContainer.setState(mainState);
                var documentsState = layout.documentLayout || layout.tiling || layout.center;
                if (documentsState) {
                    if (this._documentContainer) {
                        this._documentContainer.setState(documentsState);
                    } else {
                        this._documentContainerState = documentsState;
                        this._documentContainerPropertyDelegator.setDocumentContainerState(this._documentContainerState);
                    }
                }
                // Add panels
                Object.keys(this._panels).forEach(function (panelId) {
                    var panelInfo = this._panels[panelId];
                    var properties = panelInfo.properties;
                    if (!properties.get(ChildProperties.IS_OPEN) && !properties.isContextual &&
                        (!layout || ArrangeableBorderContainer.stateIncludes(mainState, properties.panelId))) {
                        this._addPanelToLayout(panelInfo);
                    }
                }, this);

                // If the document container hasn't been created but there is nothing to occupy the space,
                // create it
                if (!this._documentContainer && !mainState.bottom && !this._mainContainer.hasPanelInRegion("bottom")) {
                    this._addDocumentContainer();
                }

                // Restore panel scroll positions
                Object.keys(that._panels).forEach(function (panelId) {
                    var panelInfo = that._panels[panelId];
                    if (panelInfo.widget) {
                        LayoutUtils.restoreScrollPosition(panelInfo.widget, panelInfo.properties);
                    }
                });

                this._restoringLayout = false;
            }
        },

        _saveDocuments: function () {
            if (this.persistDocuments && !this._restoringDocuments) {
                var documents = [];
                this._documents.forEach(function (documentInfo) {
                    if (documentInfo.factory) {
                        documents.push({
                            title : documentInfo.properties.title || "untitled",
                            documentType: documentInfo.properties.documentType,
                            state: documentInfo.factory.getState(documentInfo.widget, documentInfo.properties),
                            factoryPath: documentInfo.factory.modulePath
                        });
                    }
                });
                this.sessionDataService.save(this._documentsKey, documents);
            }
        },

        _restoreDocuments: function () {
            var that = this;
            if (this._documentsToRestore) {
                // Can only restore documents whose type has been registered.
                this._restoringDocuments = true;
                var documentsNotRestored = [];
                var doneHandler = function() {
                    if (that._errorDuringDocumentRestore && that._documentToRestoreCount === 0 && that._documentContainer) {
                        that._documentContainer.endStateRestore();
                    }
                };
                this._documentsToRestore.forEach(function (documentInfo) {
                    // Don't have properties, but can have type
                    if (that._started && that._getPropertiesForType(documentInfo.documentType)) {
                        require([documentInfo.factoryPath], function(Factory) {
                            var factory = new Factory();
                            when(factory.createProperties(that, documentInfo.state), function (properties) {
                                when (factory.createWidget(that, documentInfo.state, properties),
                                    function (widget) {
                                        that._documentToRestoreCount--;
                                        if (widget) {
                                            that.addDocument(widget, properties);
                                        } else {
                                            that._errorDuringDocumentRestore = true;
                                        }
                                        doneHandler();
                                    },
                                    function (error) {
                                        that._documentToRestoreCount--;
                                        that._errorDuringDocumentRestore = true;
                                        doneHandler();
                                    }
                                );
                            });
                        });
                    } else {
                        documentsNotRestored.push(documentInfo);
                    }
                });
                if (documentsNotRestored.length > 0) {
                    this._documentsToRestore = documentsNotRestored;
                } else {
                    delete this._documentsToRestore;
                }
                this._restoringDocuments = false;
            }
        },

        _canRestoreLayout: function (majorVersion, minorVersion) {
            return majorVersion <= MAJOR_VERSION && minorVersion === this.minorVersion;
        },

        _titleSetter: function (title) {
            this.title = title;
            if (window.cefclient) {
                window.cefclient.sendMessage('setTitle', [this.title]);
            } else {
                var title = document.getElementsByTagName("title")[0];
                if (title) {
                    title.textContent = this.title;
                }
            }
        },

        /*
         * Returns a layout object which captures the current state of the container.
         */
        _layoutGetter: function (crossSession) {
            if (this._settingPropertyName === LAYOUT) {
                // Call is coming from within set to get the old value.  Don't bother
                return {};
            }
            var that = this;
            var layout = {
                majorVersion: MAJOR_VERSION,
                minorVersion: this.minorVersion,
                panelLayout : this._mainContainer.getState(),
                documentLayout : this._documentContainer ? this._documentContainer.getState(crossSession) : this._documentContainerState
            };
            if (!layout.documentLayout) {
                delete layout.documentLayout;
            }
            var bounds = this.get(WINDOW_BOUNDS);
            if (bounds) {
                layout.windowBounds = bounds;
            }
            if (this.hasToolstrip) {
                layout.toolstripCollapsed = this.get(IS_TOOLSTRIP_COLLAPSED);
            }
            layout.types = {};
            this._documentTypes.forEach(function (type) {
                var properties = that._getPropertiesForType(type);
                if (properties.windowBounds) {
                    layout.types[type] = {
                        windowBounds: properties.windowBounds
                    }
                }
            });
            if (Object.keys(layout.types).length === 0) {
                delete layout.types;
            }

            if (crossSession && this._contextManager) {
                layout.contexts = this._contextManager._panelManager.getContextualStates();
            }
            return layout;
        },

        /*
         * Sets the container layout.
         *
         * @param layout - a container layout object or a JSON formatted string representation of the same
         */
        _layoutSetter: function (layout) {
            if (typeof(layout) === "string") {
                layout = JSON.parse(layout);
            }
            this._restoreLayout(layout);
            this._saveLayout(); // Suppressed during restore above
        },

        _panelLayoutGetter: function () {
            if (this._settingPropertyName === PANEL_LAYOUT) {
                // Call is coming from within set to get the old value.  Don't bother
                return {};
            }
            return this._mainContainer.getState();
        },

        _panelLayoutSetter: function (panelLayout) {
            // Use layout setter rather than directly setting state on main.  The layout set response includes code
            // to add panels to main as needed.
            var layout = {
                majorVersion: MAJOR_VERSION,
                minorVersion: this.minorVersion,
                panelLayout : panelLayout
            };
            this._layoutSetter(layout);
        },

        _documentLayoutGetter: function () {
            if (this._settingPropertyName === DOCUMENT_LAYOUT) {
                // Call is coming from within set to get the old value.  Don't bother
                return {};
            }
            return this._documentContainer ? this._documentContainer.getState() : (this._documentContainerState || {});
        },

        _documentLayoutSetter: function (documentLayout) {
            var validationResult = UIContainer.validateDocumentLayout(documentLayout);
            if (!validationResult.isValid) {
                Log.error(validationResult.failureMessage);
            } else {
                if (this._documentContainer) {
                    this._documentContainer.setState(documentLayout);
                } else {
                    this._documentContainerState = documentLayout;
                }
            }
        },

        _footerGetter: function () {
            return this.isDocked ? this.parentContainer._footerGetter() :
                (this.footerSpansFullWidth ? this._outerContainer.getChildInRegion("bottom") :
                                             this._mainContainer.getFooter());
        },

        _footerSetter: function (footer) {
            if (!this.isDocked) {
                if (this.footerSpansFullWidth) {
                    if (footer) {
                        this._outerContainer.addWithProperties(footer, {region: "bottom"});
                    } else {
                        this._outerContainer.removeChildInRegion("bottom");
                    }
                } else {
                    this._mainContainer.setFooter(footer);
                }
            }
        },

        _windowBoundsSetter: function (bounds) {
            WindowUtils.applyDefaultBounds(bounds);
            WindowUtils.convertBoundsToPixels(bounds);
            WindowUtils.setWindowBounds(window, bounds);
        },

        _isVisibleSetter: function (isVisible) {
            if (window.cefclient && isVisible !== this.get(IS_VISIBLE)) {
                window.cefclient.sendMessage("setVisible", [isVisible]);
                this._changeAttrValue(IS_VISIBLE, isVisible);
            }
        },

        _isBusySetter: function (isBusy) {
            if (this.isBusy !== isBusy) {
                if (isBusy) {
                    if (!this._busyOverlay) {
                        var overlayNode = domConstruct.create("div", {id: "mwFullBusyOverlay"}, document.body);
                        this._busyOverlay = new BusyOverlay(BusyOverlay.SIZE.SMALL);
                        this._busyOverlay.set("target", overlayNode);
                    }
                    this._busyOverlay.show();
                } else if (this._busyOverlay) {
                    this._busyOverlay.hide();
                }
                this._changeAttrValue(IS_BUSY, isBusy);
            }
        },

        _isToolstripCollapsedGetter: function () {
            return this.uiBuilder.get(this._toolstripId, "displayState") !== DisplayStateConstants.EXPANDED;
        },

        _isToolstripCollapsedSetter: function (isCollapsed) {
            this.uiBuilder.set(this._toolstripId, "displayState", isCollapsed ? DisplayStateConstants.COLLAPSED : DisplayStateConstants.EXPANDED);
        },

        _selectedToolstripTabGetter: function () {
            var tabId = this.toolstrip.get("selectedTab");
            // The following works around what appears to be a bug.  After the toolstrip has been created and the global tabs
            // added the selectedTab property is empty, event though the first tab is showing.
            if (!tabId) {
                var tabs = this.getToolstripTabs();
                if (tabs.length > 0) {
                    tabId = tabs[0].id;
                }
            }
            return tabId ? {
                id: tabId,
                tag: this.uiBuilder.get(tabId, "tag"),
                title: this.uiBuilder.get(tabId, "title")
            } : null;
        },

        _selectedToolstripTabSetter: function (tab) {
            var i, tabs;
            var id = tab.id;
            if (!id) {
                if (tab.tag) {
                    id = this.uiBuilder.tagToId(tab.tag);
                } else if (tab.title) {
                    tabs = this.getToolstripTabs();
                    for (i = 0; i < tabs.length && !id; i++) {
                        if (tabs[i].title === tab.title) {
                            id = tabs[i].id;
                        }
                    }
                }
            }
            if (id) {
                this.uiBuilder.set(this._toolstripId, "selectedTab", id);
            }
        },

        _handleToolstripPropertyChange: function (changeData) {
            if (changeData.property === "displayState") {
                var oldValue = changeData.oldValue !== DisplayStateConstants.EXPANDED;
                var newValue = changeData.newValue !== DisplayStateConstants.EXPANDED;
                if (oldValue !== newValue) {
                    this.notifyStateWatchers(IS_TOOLSTRIP_COLLAPSED, oldValue, newValue);
                    UsageLogger.logEvent({
                        eventType: newValue ? "minimized" : "restored",
                        elementId: "toolstrip",
                        elementType: "toolstrip"
                    });
                    this._handleLayoutChange();
                }
            }
        },

        _handleToolstripSelectedTabChange: function (name, oldValue, newValue) {
            var oldTab = this._getToolstripTabFromId(oldValue);
            var newTab = this._getToolstripTabFromId(newValue);
            this.notifyStateWatchers(SELECTED_TOOLSTRIP_TAB, oldTab, newTab);
        },

        /**
         * @returns {array} containing the tabs currently on the toolstrip.  Each tab is described by an item containing
         *           its id, tag and title.
         * @instance
         */
        getToolstripTabs: function () {
            var that = this;
            var tabs = [];
            this.uiBuilder.getChildrenIds(this._toolstripId).forEach(function (tabGroupId) {
                that.uiBuilder.getChildrenIds(tabGroupId).forEach(function (tabId) {
                    tabs.push({
                        id: tabId,
                        tag: that.uiBuilder.get(tabId, "tag"),
                        title: that.uiBuilder.get(tabId, "title")
                    })
                });
            });
            return tabs;
        },

        _getToolstripTabFromId: function (id) {
            var tab, tabs, i;
            if (id) {
                tabs = this.getToolstripTabs();
                for (i = 0; i < tabs.length && !tab; i++) {
                    if (tabs[i].id === id) {
                        tab = tabs[i];
                    }
                }
            }
            return tab;
        },

        // Save the layout in response to a state change but defer in order to coalesce.  Note that the sessionDataService
        // may coalesce actual storage activity but the code below coalesces state gathering.
        _handleLayoutChange: function () {
            if (this.persistLayout && !this._isSavePending) {
                var that = this;
                this._isSavePending = true;
                setTimeout(function() {
                    that._saveLayout();
                    that._isSavePending = false;
                }, 0);
            }
            this.notifyStateWatchers(LAYOUT, {}, lang.hitch(this, "_layoutGetter"), 0);
        },

        _handlePanelLayoutChange: function () {
            this.notifyStateWatchers(PANEL_LAYOUT, {}, lang.hitch(this, "_panelLayoutGetter"), 0);
        },

        _handleDocumentLayoutChange: function () {
            this.notifyStateWatchers(DOCUMENT_LAYOUT, {}, lang.hitch(this, "_documentLayoutGetter"), 0);
        },

        set: function(propertyName, newValue) {
            if (propertyName === SelectionManager.SELECTED_CHILD && typeof(newValue) === "string") {
                // Selected child property value must be a widget but allow it to be identified
                // by a panelId or title
                var info = this._getChildInfo(newValue);
                if (info) {
                    newValue = info.widget;
                }
            }

            var delegate = this._propertyDelegates[propertyName];
            if (delegate) {
                delegate.set(propertyName, newValue);
            } else {
                this._settingPropertyName = propertyName;
                this.inherited(arguments);
                delete this._settingPropertyName;
            }
        },

        _createDocumentContainerPropertyDelegator: function (initialProperties) {
            var that = this;
            var delegator = lang.mixin(new Stateful(), {
                get: function (propertyName) {
                    if (that._documentContainer) {
                        return that._documentContainer.get(propertyName);
                    } else if (that._documentContainerState) {
                        return that._documentContainerState[propertyName];
                    } else {
                        return undefined;
                    }
                },

                set: function (propertyName, newValue) {
                    if (that._documentContainer) {
                        that._documentContainer.set(propertyName, newValue);
                    } else {
                        if (!that._documentContainerState) {
                            that._documentContainerState = {};
                        }
                        that._documentContainerState[propertyName] = newValue;
                        delegator._changeAttrValue(propertyName, newValue);
                    }
                },

                setDocumentContainer: function (documentContainer) {
                    if (documentContainer) {
                        var delegate = function (name, oldValue, newValue) {
                            delegator._changeAttrValue(name, newValue);
                        };
                        documentContainer.own(documentContainer.watch(DocumentContainer.TAB_POSITION, delegate));
                        documentContainer.own(documentContainer.watch(DocumentContainer.ARRANGEMENT, delegate));
                        documentContainer.own(documentContainer.watch(DocumentContainer.GRID_SIZE, delegate));
                        documentContainer.own(documentContainer.watch(DocumentContainer.ROW_WEIGHTS, delegate));
                        documentContainer.own(documentContainer.watch(DocumentContainer.COLUMN_WEIGHTS, delegate));
                        documentContainer.own(documentContainer.watch(DocumentContainer.TILE_COVERAGE, delegate));
                        documentContainer.own(documentContainer.watch(DocumentContainer.PLACE_HOLDER_TEXT, delegate));
                    }
                },

                setDocumentContainerState: function (documentContainerState) {
                    if (documentContainerState) {
                        delegator._changeAttrValue(DocumentContainer.TAB_POSITION, documentContainerState[DocumentContainer.TAB_POSITION]);
                        delegator._changeAttrValue(DocumentContainer.ARRANGEMENT, documentContainerState[DocumentContainer.ARRANGEMENT]);
                        delegator._changeAttrValue(DocumentContainer.GRID_SIZE, documentContainerState[DocumentContainer.GRID_SIZE]);
                        delegator._changeAttrValue(DocumentContainer.ROW_WEIGHTS, documentContainerState[DocumentContainer.ROW_WEIGHTS]);
                        delegator._changeAttrValue(DocumentContainer.COLUMN_WEIGHTS, documentContainerState[DocumentContainer.COLUMN_WEIGHTS]);
                        delegator._changeAttrValue(DocumentContainer.TILE_COVERAGE, documentContainerState[DocumentContainer.TILE_COVERAGE]);
                        delegator._changeAttrValue(DocumentContainer.PLACE_HOLDER_TEXT, documentContainerState[DocumentContainer.PLACE_HOLDER_TEXT]);
                    }
                }
            });
            if (initialProperties.documentLayout) {
                this._documentContainerState = initialProperties.documentLayout;
            }
            if (initialProperties.tabPosition) {
                delegator.set(DocumentContainer.TAB_POSITION, initialProperties.tabPosition);
            }
            if (initialProperties.gridSize) {
                delegator.set(DocumentContainer.GRID_SIZE, initialProperties.gridSize);
            }
            if (initialProperties.rowWeights) {
                delegator.set(DocumentContainer.ROW_WEIGHTS, initialProperties.rowWeights);
            }
            if (initialProperties.columnWeights) {
                delegator.set(DocumentContainer.COLUMN_WEIGHTS, initialProperties.columnWeights);
            }
            if (initialProperties.tileCoverage) {
                delegator.set(DocumentContainer.TILE_COVERAGE, initialProperties.tileCoverage);
            }
            if (initialProperties.placeHolderText) {
                delegator.set(DocumentContainer.PLACE_HOLDER_TEXT, initialProperties.placeHolderText);
            }
            return delegator;
        },

        get: function(propertyName) {
            var delegate = this._propertyDelegates[propertyName];
            if (delegate) {
                return delegate.get(propertyName);
            } else {
                return this.inherited(arguments);
            }
        },

        watch: function(propertyName, callback) {
            var that = this;
            if(!callback && typeof propertyName === "function") {
                // Watch all properties, including those which are delegated
                var watches = [];
                callback = propertyName;
                Object.keys(this._propertyDelegates).forEach(function (propertyName) {
                    watches.push(that._propertyDelegates[propertyName].watch(propertyName, callback));
                });
                watches.push(this.inherited(arguments));
                var handle = {};
                handle.unwatch = handle.remove = function () {
                    watches.forEach(function(watch) {watch.remove();});
                    watches.length = 0;
                };
                return handle;

            } else {
                var delegate = this._propertyDelegates[propertyName];
                if (delegate) {
                    return delegate.watch(propertyName, callback);
                } else {
                    return this.inherited(arguments);
                }
            }
        },

        _undockType: function (type, documentsToUndock) {
            var success = false;
            var i, documents;
            // Gather all the pieces that need to appear in the undocked container
            var properties = this._getPropertiesForType(type);
            if (properties.isUndocking) {
                 return success;
            }
            properties.isUndocking = true;
            if (documentsToUndock) {
                documents = [];
                documents.length = documentsToUndock.length;
                for (i = 0; i < documents.length; i++) {
                    documents[i] = this._getDocumentInfo(documentsToUndock[i]);
                }
            } else {
                documents = this._getDocumentsForType(type);
            }
            var parts = {
                panels: this._getPanelsForType(type),
                types: [type],
                documents: documents
            };
            // Don't create a child window unless there are documents to display in it.
            if (parts.documents.length == 0) {
                success = true;
            } else {
                // Make all of the documents as "undocking" so that their removal from the document container
                // won't be misinterpreted as a "close".
                parts.documents.forEach(function (documentInfo) {
                    documentInfo.properties.set("isUndocking", true);
                });

                var packingList = DockingManager.createPackingList(this, parts);
                packingList.title = properties.title;
                packingList.parent = this;
                packingList.contentType = type;

                try {
                    var undockedWindow = DockingManager.spawnContainer(this, this.undockedPage, type, packingList, properties.windowBounds);
                    if (undockedWindow) {
                        WindowUtils.addWindowBoundsListener(undockedWindow, function(bounds) {
                            properties.__respondingToBoundsChange = true;  // Block response to property change
                            properties._changeAttrValue(WINDOW_BOUNDS, bounds);
                            properties.__respondingToBoundsChange = false;
                        });
                    }
                    if (this._documentContainer) {
                        packingList.documentContainerState = this._documentContainer.getState();
                        if (documentsToUndock) {
                            for (i = 0; i < documents.length; i++) {
                                this._documentContainer.removeChild(documentsToUndock[i]);
                            }
                        } else {
                            this._documentContainer.removeGroup(type);
                        }
                    }
                    properties.set(DocumentTypeProperties.IS_DOCKED, false);
                    success = true;
                } catch(error) {
                }
            }
            properties.isUndocking = false;
            return success;
        },

        _undockDocument: function (document) {
            var that = this;
            var success = false;
            var documentInfo = this._getDocumentInfo(document);
            if (!documentInfo) {
                return success;
            }
            var properties = documentInfo.properties;

            if (properties.__dockWatch) {
                properties.__dockWatch.remove();
                delete properties.__dockWatch;
            }
            var type = properties.documentType;
            properties.isUndocking = true;

            // Gather all the pieces that need to appear in the undocked container
            var parts = {
                panels: this._getPanelsForType(type),
                types: [type],
                documents: [documentInfo]
            };
            var packingList = DockingManager.createPackingList(this, parts);
            packingList.title = properties.title;
            packingList.parent = this;
            packingList.contentType = DockingManager.SINGLE_DOCUMENT_CONTENT;
            try {
                var undockedWindow = DockingManager.spawnContainer(this, this.undockedPage, DockingManager.SINGLE_DOCUMENT_CONTENT,
                                                                   packingList, properties.windowBounds);
                properties.__undockedWindow = undockedWindow;
                if (this._documentContainer) {
                    this._documentContainer.removeChild(document);
                }
                WindowUtils.addWindowBoundsListener(undockedWindow, function(bounds) {
                    properties.__respondingToBoundsChange = true;  // Block response to property change
                    properties._changeAttrValue(WINDOW_BOUNDS, bounds);
                    properties.__respondingToBoundsChange = false;
                });
                success = true;
            } catch(error) {
                properties.isUndocking = false;
            }
            return success;
        },

        _undockChildUI: function (childId) {
            var that = this;

            // Shutdown the child container
            this._dockedContainers[childId].shutdown();

            // Recreate it undocked
            require([DockingManager.getUIFactoryPath(childId)], function(ContainerFactory) {
                that.createChildUI(childId, new ContainerFactory(), false);
            });
        },

        _addDocumentUndocked: function (documentInfo, type) {
            var success = false;
            if (type) {
                var typeProperties = this._getPropertiesForType(type);

                // Gather all the pieces that need to appear in the undocked container
                var parts = {
                    panels: this._getPanelsForType(type),
                    types: [type],
                    documents: [documentInfo]
                };
                var packingList = DockingManager.createPackingList(this, parts);
                packingList.title = typeProperties.title;
                packingList.parent = this;
                packingList.contentType = type;
                try {
                    DockingManager.sendToUndockedContainer(this, this.undockedPage, type, packingList);
                    success = true;
                } catch(error) {
                }
                return success;
            } else {
                return this._undockDocument(documentInfo.widget);
            }
        },

        _getPanelsForType: function (type) {
            var that = this;
            var typeProperties = this._getPropertiesForType(type);
            var panelIds = [];
            if (typeProperties.panelIds) {
                panelIds = panelIds.concat(typeProperties.panelIds);
            }
            if (typeProperties.contexts) {
                typeProperties.contexts.forEach(function(context) {
                    if (context.panelIds) {
                        context.panelIds.forEach(function(panelId) {
                            if (panelIds.indexOf(panelId) < 0) {
                                panelIds.push(panelId);
                            }
                        });
                    }
                });
            }
            var panelList = [];
            panelIds.forEach(function (panelId) {
                panelList.push(that._panels[panelId]);
            });
            return panelList;
        },

        _getDocumentsForType: function (type) {
            var documentList = [];
            this._documents.forEach(function(documentInfo) {
                if (documentInfo.properties.documentType === type) {
                    documentList.push(documentInfo);
                }
            });
            return documentList;
        },

        _dock: function () {
            if (this._isDocking) return;
            this._isDocking = true;
            var that = this;

            if (this.contentType == DockingManager.CHILD_UI_CONTENT) {
                // Close the ui
                this.shutdown();
                window.close();
                // Recreate it docked
                require([DockingManager.getUIFactoryPath(this.id)], function(ContainerFactory) {
                    that.parentContainer.createChildUI(that.id, new ContainerFactory(), true);
                });
                return;
            }

            // Gather all the document types, panels and documents to be returned to the parent container
            var parts = {
                panels: this._getAllPanels(),
                types: this._getDocumentTypes(),
                documents: this._getAllDocuments()
            };
                        
            parts.documents.forEach(function (documentInfo) {
                if (documentInfo.properties.__undockedWindow) {
                    delete documentInfo.properties.__undockedWindow;
                }
            });
            
            var packingList = DockingManager.createPackingList(this, parts);
            packingList.parent = this;
            packingList.contentType = this.contentType;

            // Remove all documents
            if (this._documentContainer) {
                if (this.contentType !== DockingManager.SINGLE_DOCUMENT_CONTENT) {
                    packingList.documentContainerState = this._documentContainer.getState();
                }
                this._documentContainer.removeAll();
            }
            this._getDocumentTypes().forEach(function (type){
                that._removeDocumentType(type);
            });

            DockingManager.returnToOriginalContainer(that, packingList);
            this.destroyRecursive();
            window.close();
        },

        _getAllPanels: function () {
            var that = this;
            var panels = [];
            Object.keys(this._panels).forEach(function (panelId) {
                panels.push(that._panels[panelId]);
            });
            return panels;
        },

        _getAllDocuments: function () {
            var documents = [];
            document.length = this._documents.length;
            var i;
            for (i = 0; i < this._documents.length; i++) {
                documents[i] = this._documents[i];
            }
            return documents;
        },

        /**
         * Closes the container and the containing window (if it is a CEF window).
         *
         * @param force if true container and document close approvers will not be given a chance to veto the close.
         */
        close: function (force) {
            if (force) {
                this._doClose();
            } else {
                this._canClose().then(lang.hitch(this, this._doClose));
            }
        },

        isClosing: function () {
            return this._isClosing;
        },

        _canClose: function () {
            // Check with all close approvers and documents
            var approvers = this._closeApprovers.slice(0);
            this._documents.forEach(function (documentInfo) {
                if (documentInfo.properties.canClose) {
                    approvers.push(lang.hitch(documentInfo.properties, "canClose"));
                }
            });
            return allApprove(approvers);
        },

        _doClose: function() {
            this._isClosing = true;
            ChildWindowManager.closeChildren();

            UsageLogger.logEvent({
                eventType: "closed",
                elementId: this.appId,
                elementType: "app"
            }, undefined, true /* immediate */);

            // Give children an opportunity to clean up before closing the container
            var that = this;
            Object.keys(this._panels).forEach(function (panelId) {
                CleanupManager.cleanup(that._panels[panelId].properties);
            });
            this._documents.forEach(function (documentInfo) {
                CleanupManager.cleanup(documentInfo.properties);
            });
            CleanupManager.completePendingCleanups().then(function () {
                if (window.cefclient) {
                    window.cefclient.sendMessage("close");
                }
            });
        },

        /**
         * Creates all the key bindings.
         * @private
         */
        _createKeyBindings: function () {
            // TODO: Remove this line when the focus issues are resolved.
            domClass.add(document.body, ROOT_CLASS);

            // Create navigation key binding - Ctrl + F6.
            this._createNavigationKeyBinding();

            // Create backward navigation key binding - Ctrl + Shift + F6
            this._createBackwardNavigationKeyBinding();
        },

        /**
         * Set the action to the key binding
         * @param action
         * @private
         */
        _setKeyBindingAction: function (action) {
            // Add the action in the action data service
            this.actionService.addAction(action);

            // Get the binding
            var binding = this._keyBindings.getBinding(action.shortcut);

            // Set the action on the binding
            binding.setAction(ROOT_SELECTOR, action.id);
        },

        /**
         * Creates navigation key binding - Ctrl + F6 / Command + F6.
         * @private
         */
        _createNavigationKeyBinding: function () {
            // Create new action for navigation key binding
            var navigationBindingAction = {
                id: "navigationBinding",
                enabled: true,
                shortcut: [KeyBindings.Keys.SYSCTRL, KeyBindings.Keys.F6],
                callback: lang.hitch(this, "_navigateAcrossChildren", "forward")
            };

            this._setKeyBindingAction(navigationBindingAction);
        },

        /**
         * Create backward navigation key binding - Ctrl + Shift + F6 / Command + Shift + F6.
         * @private
         */
        _createBackwardNavigationKeyBinding: function () {
            // Create new action for backward navigation key binding
            var backwardNavigationBindingAction = {
                id: "backwardNavigationBinding",
                enabled: true,
                shortcut: [KeyBindings.Keys.SYSCTRL, KeyBindings.Keys.SHIFT, KeyBindings.Keys.F6],
                callback: lang.hitch(this, "_navigateAcrossChildren", "backward")
            };

            this._setKeyBindingAction(backwardNavigationBindingAction);
        },

        _getChildPropertyButton: function (childProperty) {
            return childProperty.widget && childProperty.widget.button && childProperty.widget.button.domNode
                ? childProperty.widget.button.domNode : null;
        },

        _getChildPropertyGrandparent: function (childProperty) {
            if (childProperty.widget && childProperty.widget.getParent && childProperty.widget.getParent()
                && childProperty.widget.getParent().getParent && childProperty.widget.getParent().getParent()
                && childProperty.widget.getParent().getParent().domNode) {
                return childProperty.widget.getParent().getParent().domNode
            } else {
                return null;
            }
        },

        /**
         * For some scenarios like
         * 1. when the focus is on the expand/collapse button of the side container or bottom container.
         * 2. when side/bottom container is collapsed and then expanded or expanded first and then collapsed and the
         * focus is on the title bar / collapsed panel's button.
         * No child is selected in this case. This method returns the appropriate child that could be treated as the
         * reference/selected child to find the next child.
         * @param {HTMLElement} activeElement - currently focused HTML element
         * @param {String} direction - "forward" or "backward"
         * @returns {Object} selectedChild
         * @private
         */
        _findAppropriateSelectedChild: function (activeElement, direction) {
            var selectedChild, button, grandParent, navAssociation, matchFound = false;
            var childProperties = this._selectionManager._childProperties;

            for (var childProperty in childProperties) {
                if (childProperties.hasOwnProperty(childProperty)) {
                    if (childProperties[childProperty].widget && childProperties[childProperty].widget.navAssociation) {
                        if (childProperties[childProperty].widget.navAssociation.domNode) {
                            navAssociation = childProperties[childProperty].widget.navAssociation.domNode;
                        } else {
                            navAssociation = childProperties[childProperty].widget.navAssociation;
                        }
                    }

                    // SideContainer/BottomContainer expanded case
                    if (navAssociation && (activeElement === navAssociation || activeElement.parentNode === navAssociation)) {
                        selectedChild = childProperties[childProperty].widget;
                        break;
                    }

                    // SideContainer/BottomContainer collapsed case
                    else {
                        button = this._getChildPropertyButton(childProperties[childProperty]);
                        grandParent = this._getChildPropertyGrandparent(childProperties[childProperty]);

                        if (activeElement.classList.contains("mwCollapsedPanelButton")) {
                            if (button && activeElement === button) {
                                matchFound = true;
                            }
                        } else if (button && (activeElement.parentNode === button.parentNode || activeElement.parentNode.parentNode === grandParent)) {
                            matchFound = true;
                        }

                        if (matchFound) {
                            selectedChild = childProperties[childProperty].widget;
                            if (selectedChild.getParent) {
                                if (direction === "forward") {
                                    selectedChild = this._selectionManager._getLastChild(selectedChild.getParent(), false, true);
                                } else {
                                    selectedChild = this._selectionManager._getFirstChild(selectedChild.getParent(), false, true);
                                }
                                break;
                            }
                        }
                    }
                }
            }

            // Select the child
            if (selectedChild) {
                this._selectionManager.blockFocus();
                this._selectionManager.blockRevealChild();
                this._selectionManager.setSelected(selectedChild, true);
                this._selectionManager.unblockFocus();
                this._selectionManager.unblockRevealChild();
            }

            return selectedChild;
        },

        _getChildRegion: function (child) {
            return child.properties && child.properties.region ? child.properties.region : null;
        },

        /**
         * Callback for the navigation key binding action - Changes focus among different desktop components - panels,
         * documents and toolstrip.
         *
         * @param {string} direction - "forward" or "backward"
         * @private
         */
        _navigateAcrossChildren: function (direction) {
            // TODO: Do not rely on classes for the modal dialog check instead use an API when it is available - g1724372
            // g1766309: Keyboard Navigation among the children should not be operable when a modal dialog is open
            if (document.body.querySelector(".mwModalDialog") || document.body.querySelector(".dijitDialog")) {
                return;
            }

            var toolstrip = this._mnemonicsManager.getToolstrip();

            // If the toolstrip mnemonic state is not NULL_NULL then that means the focus is on the toolstrip. Select
            // toolstrip in this case to find the correct next child.
            if (MnemonicsStates.getCurrentMnemonicsState() !== MnemonicsStates.NULL_NULL) {
                this._selectChild(this._childProperties[TOOLSTRIP_ID]);
            }

            // Select toolstrip if the popup belongs to toolstrip.
            if (LayoutUtils.isPopupShowing()) {
                var topPopup = popup.getTopPopup();

                if (topPopup && topPopup.parent && topPopup.parent.domNode && toolstrip.domNode) {
                    if (toolstrip.domNode.contains(topPopup.parent.domNode)) {
                        this._selectChild(this._childProperties[TOOLSTRIP_ID]);
                    }
                }
            }

            // Get the selected child.
            var selectedChild = this._selectionManager[SelectionManager.SELECTED_CHILD];
            var activeElement = document.activeElement;

            if(!selectedChild && activeElement) {
                if (this._documentContainer === undefined || (this._documentContainer &&
                    !this._documentContainer.domNode.contains(activeElement))) {
                    // When the focus is on the expand/collapse all button of the SideContainer or BottomContainer,
                    // find the appropriate reference child. Directly using the last selected child as the refernce
                    // child does not work in this case in case of collapsed panels, we do not really select the panels.
                    selectedChild = this._findAppropriateSelectedChild(activeElement, direction);
                    activeElement = document.activeElement;
                }
                else {
                    // This case is for the DocumentContainer's plus button and the context menu where the reference
                    // child is the last selected child.
                    selectedChild = this._selectionManager.getLastSelectedChild();
                    if (selectedChild) {
                        this._selectionManager.blockFocus();
                        this._selectionManager.blockRevealChild();
                        this._selectionManager.setSelected(selectedChild, true);
                        this._selectionManager.unblockFocus();
                        this._selectionManager.unblockRevealChild();
                    } else {
                        return;
                    }
                }
            }

            var ignoreGroupTabCases = false;

            // Skip the following logic if the focus is on the context menu button of a tab group
            if (activeElement.parentNode.querySelector(".groupTab") === null) {
                // Move focus from the content of the container to the titlebar/tab.
                if (selectedChild.domNode && selectedChild.domNode.contains(activeElement) && selectedChild.navAssociation) {
                    if (selectedChild.navAssociation.focus) {
                        selectedChild.navAssociation.focus();
                        // Return when the navAssociation received the focus.
                        if (activeElement !== document.activeElement) {
                            return;
                        }
                    } else if (selectedChild.navAssociation.domNode) {
                        selectedChild.navAssociation.domNode.focus();
                        if (activeElement !== document.activeElement) {
                            return;
                        }
                    }
                }

                // g2055627: There would be some components (ex. AddressBar in MATLAB Online) where UIContainer will
                // not have knowledge of its internal details in cases where focus needs to be moved from content to
                // the container. In that case, call this API to relay the focus appropriately.
                if (selectedChild.focusContainer) {
                    selectedChild.focusContainer();
                    if (activeElement !== document.activeElement) {
                        return;
                    }
                }

            } else {
                if (!activeElement.classList.contains("groupTab")) {
                    ignoreGroupTabCases = true;
                }
            }

            // Remove the toolstrip listeners if toolstrip is currently selected, because we now would want to select
            // the next child in the navigation cycle.
            if (selectedChild.id === TOOLSTRIP_ID) {
                this._mnemonicsManager.removeFocusFromToolstrip();
                if (toolstrip.get("displayState") === "expanded_on_top") {
                    toolstrip.set("displayState", DisplayStateConstants.COLLAPSED);
                }

                // Close any open pop ups.
                if (LayoutUtils.isPopupShowing()) {
                    popup.close();
                }
            }

            while (document.activeElement === activeElement) {
                // Get the next child to be selected.
                var nextChildToBeSelected = this._getNextChildToBeSelected(direction, selectedChild);

                if (nextChildToBeSelected) {
                    var nextChildToBeSelectedRegion = this._getChildRegion(nextChildToBeSelected);

                    // next child to be selected is in a collapsed panel
                    if (nextChildToBeSelected.button && nextChildToBeSelected.button.domNode
                        && nextChildToBeSelectedRegion && this._mainContainer.isCollapsed(nextChildToBeSelectedRegion)) {

                        var selectedChildProperties;

                        // We only need selectedChildProperties for DocumentContainer children to check the
                        // isInGroupTab property
                        if (this._documentContainer) {
                            selectedChildProperties = this._documentContainer.getProperties(selectedChild);
                        }

                        // If the activeElement is a child outside of DocumentContainer or is the group tab within
                        // DocumentContainer, deselect the current selected child and give focus to the appropriate
                        // button in the next collapsed container. We do not select the child as it will expand the
                        // collapsed panel.
                        if (selectedChildProperties === undefined || (selectedChildProperties && !selectedChildProperties.isInGroupTab)
                            || activeElement.classList.contains("groupTab")) {
                            this._selectionManager.blockSuccession();
                            this._selectionManager.setSelected(selectedChild, false);
                            this._selectionManager.unblockSuccession();

                            nextChildToBeSelected.button.domNode.tabIndex = -1;
                            nextChildToBeSelected.button.domNode.focus();
                            return;
                        }
                    }

                    if (nextChildToBeSelected.id === TOOLSTRIP_ID) {
                        this._mnemonicsManager.setFocusOnCurrentToolstripTab();

                        // g2025983: We just want to return here so that the toolstrip does not get selected. This
                        // will ensure that the widgets in the toolstrip relevant to the current selected container
                        // would still remain enabled. Selecting the toolstrip might disable these widgets.
                        return;
                    }

                    if (nextChildToBeSelected.preventContentFocusOnKeyNav || nextChildToBeSelected.focusContainer) {
                        this._selectionManager.blockFocus(); // prevents SelectionManager from focusing the content
                                                             // by not calling the focus method.
                    }

                    this._selectionManager.blockRevealChild();

                    // Select the next child.
                    this._selectChild(nextChildToBeSelected);

                    // g2055627: There would be some components (ex. AddressBar in MATLAB Online) where UIContainer will
                    // not have knowledge of its internal details in cases where focus needs to be moved to the container.
                    // In that case, call this API to relay the focus appropriately.
                    if (nextChildToBeSelected.focusContainer) {
                        nextChildToBeSelected.focusContainer();
                    }

                    if (nextChildToBeSelected.preventContentFocusOnKeyNav || nextChildToBeSelected.focusContainer) {
                        this._selectionManager.unblockFocus();
                    }

                    this._selectionManager.unblockRevealChild();

                    this._handleDocumentContainerTabCases(selectedChild, nextChildToBeSelected, activeElement, ignoreGroupTabCases);

                    // get the updated selectedChild
                    selectedChild = this._selectionManager[SelectionManager.SELECTED_CHILD];
                }
                else {
                    return;
                }
            }
        },

        _handleDocumentContainerTabCases: function (selectedChild, nextChildToBeSelected, activeElement, ignoreGroupTabCases) {
            if (ignoreGroupTabCases) {
                // activeElement is not a group tab, move focus to the titlebar or tab
                if (nextChildToBeSelected.navAssociation && nextChildToBeSelected.navAssociation.focus) {
                    nextChildToBeSelected.navAssociation.focus();
                } else if (nextChildToBeSelected.navAssociation && nextChildToBeSelected.navAssociation.domNode) {
                    nextChildToBeSelected.navAssociation.domNode.focus();
                }
                return;
            }

            var selectedChildProperties, nextChildToBeSelectedProperties;

            if (this._documentContainer) {
                selectedChildProperties = this._documentContainer.getProperties(selectedChild);
                nextChildToBeSelectedProperties = this._documentContainer.getProperties(nextChildToBeSelected);
            }

            // Both selectedChild and nextChildToBeSelected belong to some tab group
            if (selectedChildProperties && selectedChildProperties.isInGroupTab
                && nextChildToBeSelectedProperties && nextChildToBeSelectedProperties.isInGroupTab) {

                // Both selectedChild and nextChildToBeSelected are in the same tab group
                if (this._isCurrentAndNextChildInSameTabGroup(selectedChild, nextChildToBeSelected)) {

                    // If the focus is on the group tab
                    if (activeElement.classList.contains("groupTab")) {
                        this._focusGroupTab(nextChildToBeSelected);
                    } else {
                        // If the focus is not on the group tab then give focus to the next tab in the same tab group
                        if (nextChildToBeSelected.navAssociation && nextChildToBeSelected.navAssociation.focus) {
                            nextChildToBeSelected.navAssociation.focus();
                        }
                    }
                }

                // If both selectedChild and nextChildToBeSelected are not in the same tab group and focus is not
                // on the group tab, then move the focus to the parent group tab.
                else if (!activeElement.classList.contains("groupTab")) {
                    this._selectChild(selectedChild);
                    this._focusGroupTab(selectedChild);
                }

                else {
                    // If both selectedChild and nextChildToBeSelected are not in the same tab group and focus is on the
                    // parent group tab of the selectedChild, then move the focus to the group tab for
                    // nextChildToBeSelected.
                    if (nextChildToBeSelectedProperties && nextChildToBeSelectedProperties.isInGroupTab && nextChildToBeSelected.getParent) {
                        this._focusGroupTab(nextChildToBeSelected);
                    }
                }

            }

            // If selectedChild is the last child in a tab group and the nextChildToBeSelected is not in a tab
            // group and the focus is not on the parent group tab of the selectedChild, then move focus
            // from the last child in the tab group to the parent group tab.
            else if (selectedChildProperties && selectedChildProperties.isInGroupTab && !activeElement.classList.contains("groupTab")) {
                this._selectChild(selectedChild);
                this._focusGroupTab(selectedChild);
            }

            else {
                // If the nextChildToBeSelected is within a tab group, then move focus to its parent group tab.
                if (nextChildToBeSelectedProperties && nextChildToBeSelectedProperties.isInGroupTab && nextChildToBeSelected.getParent) {
                    this._focusGroupTab(nextChildToBeSelected);
                }

                // Non sub tile case. Transfer focus to the tab bar.
                else if (nextChildToBeSelected.navAssociation && nextChildToBeSelected.navAssociation.focus) {
                    nextChildToBeSelected.navAssociation.focus();
                } else if (nextChildToBeSelected.navAssociation && nextChildToBeSelected.navAssociation.domNode) {
                    nextChildToBeSelected.navAssociation.domNode.focus();
                }
            }
        },

        _focusGroupTab: function (child) {
            if (child.getParent) {
                var parent = child.getParent();
                while (!parent.navAssociation) {
                    parent = parent.getParent();
                }

                if (parent.navAssociation.focus) {
                    parent.navAssociation.focus();
                }
            }
        },

        /**
         * Checks if the current child and the next child are within the same DocumentContainer tab group.
         * @param {Object} currentChild - currently selected child
         * @param {Object} nextChild - next child to be selected
         * @returns {boolean}
         * @private
         */
        _isCurrentAndNextChildInSameTabGroup: function (currentChild, nextChild) {
            var tiles = this._documentContainer.getChildrenInTileOrder();
            var currentChildFound = false;
            var nextChildFound = false;

            for (var tileIndex = 0; tileIndex < tiles.length; tileIndex++) {
                if (tiles[tileIndex].domNode && currentChild.domNode && nextChild.domNode){
                    if (tiles[tileIndex].domNode.contains(currentChild.domNode)) {
                        currentChildFound = true;
                    }
                    if (tiles[tileIndex].domNode.contains(nextChild.domNode)) {
                        nextChildFound = true;
                    }
                }

                if (currentChildFound && nextChildFound) {
                    return true;
                } else if (currentChildFound || nextChildFound) {
                    return false;
                }
            }
        },

        /**
         * Returns the next child to be selected.
         * @param {string} direction - "forward" or "backward"
         * @param {Object} selectedChild - the current selected child
         * @returns {Object} nextChildToBeSelected
         * @private
         */
        _getNextChildToBeSelected: function (direction, selectedChild) {
            var nextChildToBeSelected;
            if (direction === "forward") {
                nextChildToBeSelected = this._selectionManager.getNextChild(selectedChild, false, true);
            } else {
                nextChildToBeSelected = this._selectionManager.getPreviousChild(selectedChild, false, true);
            }
            return nextChildToBeSelected;
        },

        /**
         * Selects the child using selection manager
         * @param {Object} nextChildToBeSelected
         * @private
         */
        _selectChild: function (nextChildToBeSelected) {
            if (nextChildToBeSelected.widget) {
                this._selectionManager.setSelected(nextChildToBeSelected.widget, true);
            }
            else {
                this._selectionManager.setSelected(nextChildToBeSelected, true);
            }
        },

        _logDocumentOpen: function(documentProperties, isOpen) {
            UsageLogger.logEvent({
                eventType: isOpen ? "opened" : "closed",
                elementId: documentProperties.documentType + ":redacted",
                redactedHash: UsageLogger.hashString(documentProperties.documentId || documentProperties.title),
                elementType: "document"
            });
        }
    });

    /**
     * Returns true if the given container state object includes the specified panel id
     */
    UIContainer.stateIncludes = function (state, panelId) {
        var mainState = state.panelLayout || state.borders || state.main;
        return mainState && ArrangeableBorderContainer.stateIncludes(mainState, panelId);
    };

    UIContainer.validateDocumentLayout = function (documentLayout) {
        return DocumentContainer.validateState(documentLayout);
    };

    UIContainer.windowContainsASingleUndockedDocument = function () {
        return !!(DockingManager.getContainer() && DockingManager.getContainer().contentType === DockingManager.SINGLE_DOCUMENT_CONTENT);
    };

    // Constant name definitions for dynamic properties
    UIContainer.BOTTOM_HEIGHT = ArrangeableBorderContainer.BOTTOM_HEIGHT;
    UIContainer.DOCUMENT_COLUMN_WEIGHTS = DocumentContainer.COLUMN_WEIGHTS;
    UIContainer.DOCUMENT_GRID_SIZE = DocumentContainer.GRID_SIZE;
    UIContainer.DOCUMENT_LAYOUT = DOCUMENT_LAYOUT;
    UIContainer.DOCUMENT_PLACE_HOLDER_TEXT = DocumentContainer.PLACE_HOLDER_TEXT;
    UIContainer.DOCUMENT_ROW_WEIGHTS = DocumentContainer.ROW_WEIGHTS;
    UIContainer.DOCUMENT_TAB_POSITION = DocumentContainer.TAB_POSITION;
    UIContainer.DOCUMENT_TILE_COVERAGE = DocumentContainer.TILE_COVERAGE;
    UIContainer.FOOTER = FOOTER;
    UIContainer.IS_BOTTOM_COLLAPSED = ArrangeableBorderContainer.IS_BOTTOM_COLLAPSED;
    UIContainer.IS_BUSY = IS_BUSY;
    UIContainer.IS_LEFT_COLLAPSED = ArrangeableBorderContainer.IS_LEFT_COLLAPSED;
    UIContainer.IS_RIGHT_COLLAPSED = ArrangeableBorderContainer.IS_RIGHT_COLLAPSED;
    UIContainer.IS_TOOLSTRIP_COLLAPSED = IS_TOOLSTRIP_COLLAPSED;
    UIContainer.IS_VISIBLE = IS_VISIBLE;
    UIContainer.LAST_SELECTED_DOCUMENT = LAST_SELECTED_DOCUMENT;
    UIContainer.LAYOUT = LAYOUT;
    UIContainer.LEFT_WIDTH = ArrangeableBorderContainer.LEFT_WIDTH;
    UIContainer.PANEL_LAYOUT = PANEL_LAYOUT;
    UIContainer.REGION_IS_COLLAPSED = ArrangeableBorderContainer.REGION_IS_COLLAPSED;
    UIContainer.RIGHT_WIDTH = ArrangeableBorderContainer.RIGHT_WIDTH;
    UIContainer.SELECTED_CHILD = SelectionManager.SELECTED_CHILD;
    UIContainer.SELECTED_TOOLSTRIP_TAB = SELECTED_TOOLSTRIP_TAB;
    UIContainer.TITLE = TITLE;
    UIContainer.WINDOW_BOUNDS = WINDOW_BOUNDS;

    /**
     * @deprecated
     */
    UIContainer.DOCUMENT_ARRANGEMENT = DocumentContainer.ARRANGEMENT;

    // Events emitted by the UIContainer
    UIContainer.STARTING = STARTING;  // No event data
    UIContainer.STARTED = STARTED;    // "    "    "
    UIContainer.STOPPING = STOPPING;  // "    "    "
    UIContainer.STOPPED = STOPPED;    // "    "    "
    UIContainer.CHILD_ADDED = LayoutContainerEvent.CHILD_ADDED;
    UIContainer.CHILD_REMOVED = LayoutContainerEvent.CHILD_REMOVED;
    // Event data for CHILD_ADDED and CHILD_REMOVED: {
    //    childType: ("panel", "document" or "documentType")
    //    widget: only for panels and documents
    //    typeId: only for documentTypes
    //    properties:
    // }

    exports.UIContainer = UIContainer;
    exports.CHILD_ADDED  = UIContainer.CHILD_ADDED;
    exports.CHILD_REMOVED = UIContainer.CHILD_REMOVED;

    exports.BOTTOM_HEIGHT = UIContainer.BOTTOM_HEIGHT;
    exports.DOCUMENT_COLUMN_WEIGHTS = UIContainer.DOCUMENT_COLUMN_WEIGHTS;
    exports.DOCUMENT_GRID_SIZE = UIContainer.DOCUMENT_GRID_SIZE;
    exports.DOCUMENT_LAYOUT = UIContainer.DOCUMENT_LAYOUT;
    exports.DOCUMENT_PLACE_HOLDER_TEXT = UIContainer.DOCUMENT_PLACE_HOLDER_TEXT;
    exports.DOCUMENT_ROW_WEIGHTS = UIContainer.DOCUMENT_ROW_WEIGHTS;
    exports.DOCUMENT_TAB_POSITION = UIContainer.DOCUMENT_TAB_POSITION;
    exports.DOCUMENT_TILE_COVERAGE = UIContainer.DOCUMENT_TILE_COVERAGE;
    exports.IS_BOTTOM_COLLAPSED = UIContainer.IS_BOTTOM_COLLAPSED;
    exports.IS_BUSY = UIContainer.IS_BUSY;
    exports.IS_LEFT_COLLAPSED = UIContainer.IS_LEFT_COLLAPSED;
    exports.IS_RIGHT_COLLAPSED = UIContainer.IS_RIGHT_COLLAPSED;
    exports.IS_TOOLSTRIP_COLLAPSED = UIContainer.IS_TOOLSTRIP_COLLAPSED;
    exports.IS_VISIBLE = UIContainer.IS_VISIBLE;
    exports.LAST_SELECTED_DOCUMENT = UIContainer.LAST_SELECTED_DOCUMENT;
    exports.LAYOUT = UIContainer.LAYOUT;
    exports.LEFT_WIDTH = UIContainer.LEFT_WIDTH;
    exports.PANEL_LAYOUT = UIContainer.PANEL_LAYOUT;
    exports.RIGHT_WIDTH = UIContainer.RIGHT_WIDTH;
    exports.SELECTED_CHILD = UIContainer.SELECTED_CHILD;
    exports.SELECTED_TOOLSTRIP_TAB = UIContainer.SELECTED_TOOLSTRIP_TAB;
    exports.TITLE = UIContainer.TITLE;
    exports.WINDOW_BOUNDS = UIContainer.WINDOW_BOUNDS;

    return UIContainer;
});
